import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Save, ChevronLeft, ChevronRight, Printer, Check, Undo2, Redo2, ArrowUpDown, PauseCircle, Info, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, subDays, parseISO } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { ColumnNotePopover } from "@/components/ColumnNotePopover";
import { VoiceDictationButton } from "@/components/VoiceDictationButton";
import { ExportButton } from "@/components/ExportButton";
import { SheetScanButton } from "@/components/SheetScanDialog";
import { useVoiceDictation } from "@/hooks/useVoiceDictation";
import { readSharedDate, writeSharedDate } from "@/lib/sharedDate";
import { DateWarningDialog, isNotRecentDay } from "@/components/DateWarningDialog";
import { parseVoiceCommand } from "@/lib/parseVoiceCommand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DENOMINATIONS = [1, 2, 3, 5, 10, 20, 30, 50];

const DEFAULT_PACK_SIZES = {
  1: 300, 2: 150, 3: 100, 5: 100, 10: 60, 20: 30, 30: 30, 50: 30
};

const MAX_SLOTS = 30;

const MACHINE_ROWS = ['onlinePays', 'onlineTotal', 'instantGrandTotal', 'activations'];
const MACHINE_ROW_LABELS = { onlinePays: 'Online Pays', onlineTotal: 'Online Total', instantGrandTotal: 'Instant P/O Grand Total', activations: 'Activations' };
const SALES_ROWS = ['instantSale', 'onlineSale', 'instantPaidOut', 'onlinePaidOut'];
const SALES_ROW_LABELS = { instantSale: 'Instant Sale', onlineSale: 'Online Sale', instantPaidOut: 'Instant Paid Out', onlinePaidOut: 'Online Paid Out' };

const FADE_IN = { initial: { opacity: 0 }, animate: { opacity: 1 } };

export default function DailyEntry() {
  const { date: paramDate } = useParams();
  const navigate = useNavigate();
  const initialDateSourceRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (paramDate) { initialDateSourceRef.current = 'param'; return parseISO(paramDate); }
    const shared = readSharedDate();
    if (shared) { initialDateSourceRef.current = 'shared'; return shared; }
    return new Date();
  });
  
  // Running numbers grid: { denomination: [slot values] }
  const [runningNumbers, setRunningNumbers] = useState({});
  const [yesterdayNumbers, setYesterdayNumbers] = useState({});
  const [yesterdayInventory, setYesterdayInventory] = useState({});
  
  // Dynamic pack sizes from admin settings
  const [PACK_SIZES, setPackSizes] = useState(DEFAULT_PACK_SIZES);
  
  // Per-slot pack quantities for variable-size packs ($20, $30, $50)
  const [packQuantities, setPackQuantities] = useState({});
  const [yesterdayPackQuantities, setYesterdayPackQuantities] = useState({});
  
  // Inventory data: { denomination: { delivery, ticketAdded, ticketReturned, lvm, endingInventory } }
  const [inventory, setInventory] = useState({});

  // Global column-header notes (admin-editable). Map: { noteKey: noteText }
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const [columnNotes, setColumnNotes] = useState({});
  useEffect(() => {
    axios.get(`${API}/column-notes`)
      .then((res) => setColumnNotes(res.data?.notes || {}))
      .catch(() => { /* ignore — popover will show "no note yet" */ });
  }, []);
  const saveColumnNote = async (noteKey, value) => {
    const next = { ...columnNotes, [noteKey]: value };
    try {
      await axios.put(`${API}/admin/column-notes`, { notes: next });
      setColumnNotes(next);
      toast.success("Column note saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save note");
    }
  };
  
  // Machine data: { onlinePays, onlineTotal, instantGrandTotal, activations } × { machine1, machine2 }
  const [machineData, setMachineData] = useState(() => {
    const init = {};
    MACHINE_ROWS.forEach(r => { init[r] = { machine1: '', machine2: '' }; });
    return init;
  });

  // Track manually expanded rows per denomination (for skipped blank cells)
  const [expandedRows, setExpandedRows] = useState({});

  // Track which qty cells are visible (show for 3s on load/focus, then hide)
  const [visibleQtyCells, setVisibleQtyCells] = useState({});
  const qtyTimers = useRef({});

  // User preference: whether to show tiny pack-quantity cells globally
  const [showQtyCells, setShowQtyCells] = useState(false);

  // Sales comparison data
  const [salesComparison, setSalesComparison] = useState(() => {
    const init = {};
    SALES_ROWS.forEach(r => { init[r] = { actual: '', register: '' }; });
    return init;
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved'
  const [isPaused, setIsPaused] = useState(false);
  const [pauseMessage, setPauseMessage] = useState('');
  const [isFirstDay, setIsFirstDay] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [focusedMachineCell, setFocusedMachineCell] = useState(null);
  // Tracks which Sales/Inventory register cell is currently focused, so we
  // can show the raw editable value while typing but show formatted (X) for
  // negative values when unfocused.
  const [focusedSalesCell, setFocusedSalesCell] = useState(null);
  const [focusedInventoryCell, setFocusedInventoryCell] = useState(null);
  // Clear-page dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [clearing, setClearing] = useState(false);
  const autoSaveTimer = useRef(null);
  const isInitialLoad = useRef(true);
  // Deep data-loss protection refs:
  // - latestStateRef: always the freshest snapshot of user inputs, used
  //   by the flush-on-hide handler and by the serialized-save re-fire.
  // - saveInFlightRef: true while a POST is in flight; prevents concurrent
  //   saves whose responses could race and clobber each other.
  // - pendingSaveRef: set when a save is requested while another is in
  //   flight — we fire one more save (with latest state) after the current
  //   one completes, so nothing is left un-persisted.
  // - lastSavedAtRef: server-confirmed `updated_at` timestamp; used to
  //   decide whether a localStorage draft is newer than what's on server.
  const latestStateRef = useRef({
    runningNumbers: {}, inventory: {}, machineData: {},
    salesComparison: {}, packQuantities: {},
  });
  const saveInFlightRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const lastSavedAtRef = useRef(0);
  // Monotonic fetch sequence: ignores stale /api/entry responses (StrictMode
  // double-mount or rapid date switches) so an older response can never
  // overwrite newer state (e.g. applied scanned closing numbers).
  const fetchSeqRef = useRef(0);

  // Swap yesterday's carry-forward values
  const [swapMode, setSwapMode] = useState(false);
  const [swapFirst, setSwapFirst] = useState(null); // { denom, slotIdx }

  const handleSwapClick = (denom, slotIdx) => {
    if (!swapMode) return;
    if (!swapFirst) {
      setSwapFirst({ denom, slotIdx });
    } else {
      if (swapFirst.denom !== denom) {
        toast.error("Select two cells in the same column");
        setSwapFirst(null);
        return;
      }
      if (swapFirst.slotIdx === slotIdx) {
        setSwapFirst(null);
        return;
      }
      // Swap yesterday's values
      saveUndoPoint();
      setYesterdayNumbers(prev => {
        const newArr = [...(prev[denom] || [])];
        const temp = newArr[swapFirst.slotIdx];
        newArr[swapFirst.slotIdx] = newArr[slotIdx];
        newArr[slotIdx] = temp;
        return { ...prev, [denom]: newArr };
      });
      toast.success(`Swapped row ${swapFirst.slotIdx + 1} ↔ row ${slotIdx + 1} for $${denom}`);
      setSwapFirst(null);
      setSwapMode(false);
    }
  };

  // Undo/Redo - uses refs for stacks to avoid stale closures
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const isUndoRedo = useRef(false);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);

  // We store the "current known state" in a ref so update handlers can snapshot it
  const stateRef = useRef({ runningNumbers, inventory, machineData, salesComparison, packQuantities });
  useEffect(() => {
    if (!isInitialLoad.current && !isUndoRedo.current) {
      stateRef.current = { runningNumbers, inventory, machineData, salesComparison, packQuantities };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningNumbers, inventory, machineData, salesComparison, packQuantities]);

  // Push current state to undo stack before a change
  const saveUndoPoint = () => {
    if (isUndoRedo.current || isInitialLoad.current) return;
    const snap = JSON.parse(JSON.stringify(stateRef.current));
    // Deduplicate: don't push if identical to last entry
    const last = undoStack.current[undoStack.current.length - 1];
    if (last && JSON.stringify(last) === JSON.stringify(snap)) return;
    undoStack.current.push(snap);
    if (undoStack.current.length > 50) undoStack.current.shift();
    redoStack.current = [];
    setUndoCount(undoStack.current.length);
    setRedoCount(0);
  };

  const handleUndo = () => {
    if (undoStack.current.length === 0) return;
    isUndoRedo.current = true;
    // Save current state to redo
    redoStack.current.push(JSON.parse(JSON.stringify(stateRef.current)));
    // Pop and apply
    const prev = undoStack.current.pop();
    setRunningNumbers(prev.runningNumbers);
    setInventory(prev.inventory);
    setMachineData(prev.machineData);
    setSalesComparison(prev.salesComparison);
    if (prev.packQuantities) setPackQuantities(prev.packQuantities);
    stateRef.current = prev;
    setUndoCount(undoStack.current.length);
    setRedoCount(redoStack.current.length);
    setTimeout(() => { isUndoRedo.current = false; }, 300);
  };

  const handleRedo = () => {
    if (redoStack.current.length === 0) return;
    isUndoRedo.current = true;
    // Save current state to undo
    undoStack.current.push(JSON.parse(JSON.stringify(stateRef.current)));
    // Pop and apply
    const next = redoStack.current.pop();
    setRunningNumbers(next.runningNumbers);
    setInventory(next.inventory);
    setMachineData(next.machineData);
    setSalesComparison(next.salesComparison);
    if (next.packQuantities) setPackQuantities(next.packQuantities);
    stateRef.current = next;
    setUndoCount(undoStack.current.length);
    setRedoCount(redoStack.current.length);
    setTimeout(() => { isUndoRedo.current = false; }, 300);
  };

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  useEffect(() => {
    isInitialLoad.current = true;
    undoStack.current = [];
    redoStack.current = [];
    setUndoCount(0);
    setRedoCount(0);
    userHasEdited.current = false;
    fetchEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  // Fetch user preferences (show_qty_cells) once on mount; also re-sync when window regains focus
  useEffect(() => {
    let cancelled = false;
    const loadPrefs = async () => {
      try {
        const res = await axios.get(`${API}/user/settings`);
        if (!cancelled && typeof res.data?.show_qty_cells === 'boolean') {
          setShowQtyCells(res.data.show_qty_cells);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to load user prefs:', err);
      }
    };
    loadPrefs();
    window.addEventListener('focus', loadPrefs);
    return () => { cancelled = true; window.removeEventListener('focus', loadPrefs); };
  }, []);

  // Toggle the "show pack qty cells" preference inline from the toolbar.
  // Optimistically updates UI and persists to the server; reverts on error.
  const handleToggleShowQty = useCallback(async (checked) => {
    const next = !!checked;
    setShowQtyCells(next);
    try {
      await axios.put(`${API}/user/settings`, { show_qty_cells: next });
    } catch (err) {
      setShowQtyCells(!next);
      toast.error(err.response?.data?.detail || "Failed to update preference");
    }
  }, []);

  useEffect(() => {
    navigate(`/entry/${dateStr}`, { replace: true });
  }, [dateStr, navigate]);

  // Keep the shared working date in sync so Store Paperwork opens on the same date
  useEffect(() => {
    writeSharedDate(dateStr);
  }, [dateStr]);

  // Fresh open (no URL date, no shared date): jump to the most recent entered data
  useEffect(() => {
    if (initialDateSourceRef.current) return;
    const initialDateStr = format(new Date(), "yyyy-MM-dd");
    let cancelled = false;
    axios.get(`${API}/entry-latest-date`).then(res => {
      const d = res.data?.date;
      if (cancelled || !d) return;
      if (d > initialDateStr) return; // never auto-jump to a future-dated entry
      const parsed = parseISO(d);
      if (isNaN(parsed.getTime())) return;
      setSelectedDate(prev => format(prev, "yyyy-MM-dd") === initialDateStr ? parsed : prev);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEntry = async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/entry/${dateStr}`);
      if (seq !== fetchSeqRef.current) return; // stale response — a newer fetch owns the state
      const data = res.data;
      
      // Initialize running numbers
      const newRunning = {};
      const newYesterday = {};
      DENOMINATIONS.forEach(d => {
        newRunning[d] = Array(MAX_SLOTS).fill('');
        newYesterday[d] = Array(MAX_SLOTS).fill('');
      });
      
      // Load admin-configured pack sizes
      if (data.packSizes) {
        const ps = {};
        DENOMINATIONS.forEach(d => {
          ps[d] = parseInt(data.packSizes[d]) || DEFAULT_PACK_SIZES[d];
        });
        setPackSizes(ps);
      }
      
      // Load saved data
      if (data.runningNumbers) {
        Object.keys(data.runningNumbers).forEach(d => {
          if (newRunning[d]) {
            data.runningNumbers[d].forEach((val, idx) => {
              if (idx < MAX_SLOTS) newRunning[d][idx] = val || '';
            });
          }
        });
      }
      
      if (data.yesterdayNumbers) {
        Object.keys(data.yesterdayNumbers).forEach(d => {
          if (newYesterday[d]) {
            data.yesterdayNumbers[d].forEach((val, idx) => {
              if (idx < MAX_SLOTS) newYesterday[d][idx] = val || '';
            });
          }
        });
      }
      
      setRunningNumbers(newRunning);
      setYesterdayNumbers(newYesterday);
      setYesterdayInventory(data.yesterdayInventory || {});
      setIsFirstDay(!!data.isFirstDay);
      
      // Initialize pack quantities for variable-size denominations
      const newPQ = {};
      const newYPQ = {};
      DENOMINATIONS.forEach(d => {
        newPQ[d] = Array(MAX_SLOTS).fill('');
        newYPQ[d] = Array(MAX_SLOTS).fill('');
      });
      if (data.packQuantities) {
        Object.keys(data.packQuantities).forEach(d => {
          if (newPQ[d]) {
            data.packQuantities[d].forEach((val, idx) => {
              if (idx < MAX_SLOTS) newPQ[d][idx] = val || '';
            });
          }
        });
      }
      if (data.yesterdayPackQuantities) {
        Object.keys(data.yesterdayPackQuantities).forEach(d => {
          if (newYPQ[d]) {
            data.yesterdayPackQuantities[d].forEach((val, idx) => {
              if (idx < MAX_SLOTS) newYPQ[d][idx] = val || '';
            });
          }
        });
      }
      setPackQuantities(newPQ);
      setYesterdayPackQuantities(newYPQ);
      
      // Initialize inventory
      const newInventory = {};
      DENOMINATIONS.forEach(d => {
        newInventory[d] = {
          delivery: data.inventory?.[d]?.delivery || '',
          ticketAdded: data.inventory?.[d]?.ticketAdded || '',
          ticketReturned: data.inventory?.[d]?.ticketReturned || '',
          lvm: data.inventory?.[d]?.lvm || '',
          lvm2: data.inventory?.[d]?.lvm2 || '',
          endingInventory: data.inventory?.[d]?.endingInventory || '',
          registerSale: data.inventory?.[d]?.registerSale || ''
        };
      });
      setInventory(newInventory);
      
      // Load machine data
      if (data.machineData) {
        const md = {};
        MACHINE_ROWS.forEach(r => { md[r] = { machine1: data.machineData[r]?.machine1 || '', machine2: data.machineData[r]?.machine2 || '' }; });
        setMachineData(md);
      } else {
        const md = {};
        MACHINE_ROWS.forEach(r => { md[r] = { machine1: '', machine2: '' }; });
        setMachineData(md);
      }
      
      // Load sales comparison data
      if (data.salesComparison) {
        const sc = {};
        SALES_ROWS.forEach(r => { sc[r] = { actual: data.salesComparison[r]?.actual || '', register: data.salesComparison[r]?.register || '' }; });
        setSalesComparison(sc);
      } else {
        const sc = {};
        SALES_ROWS.forEach(r => { sc[r] = { actual: '', register: '' }; });
        setSalesComparison(sc);
      }

      // Cache the server's updated_at so we can decide whether a local
      // draft is newer than the server's copy (see draft-restore below).
      const serverUpdatedAtMs = (() => {
        const iso = data?.updated_at;
        if (!iso) return 0;
        const t = Date.parse(iso);
        return Number.isFinite(t) ? t : 0;
      })();
      lastSavedAtRef.current = serverUpdatedAtMs;

      // Draft restoration: if the user has un-persisted local edits (from a
      // previous session that closed/crashed/backgrounded before save
      // completed), restore them. Only accept drafts newer than the server
      // and belonging to this exact date. Show a toast + immediately
      // schedule a save so the recovery is written to the server ASAP.
      try {
        const raw = localStorage.getItem(`dailyentry_draft_${dateStr}`);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft && draft.date === dateStr && Number(draft.ts) > serverUpdatedAtMs) {
            // Restore each grid, defaulting to server-loaded values for
            // anything the draft doesn't have.
            if (draft.runningNumbers) setRunningNumbers(prev => {
              const merged = { ...prev };
              DENOMINATIONS.forEach(d => {
                if (Array.isArray(draft.runningNumbers[d])) {
                  merged[d] = Array(MAX_SLOTS).fill('').map((_, i) => draft.runningNumbers[d][i] || '');
                }
              });
              return merged;
            });
            if (draft.inventory) setInventory(prev => {
              const merged = { ...prev };
              DENOMINATIONS.forEach(d => {
                if (draft.inventory[d]) merged[d] = { ...(merged[d] || {}), ...draft.inventory[d] };
              });
              return merged;
            });
            if (draft.machineData) setMachineData(prev => ({ ...prev, ...draft.machineData }));
            if (draft.salesComparison) setSalesComparison(prev => ({ ...prev, ...draft.salesComparison }));
            if (draft.packQuantities) setPackQuantities(prev => {
              const merged = { ...prev };
              DENOMINATIONS.forEach(d => {
                if (Array.isArray(draft.packQuantities[d])) {
                  merged[d] = Array(MAX_SLOTS).fill('').map((_, i) => draft.packQuantities[d][i] || '');
                }
              });
              return merged;
            });
            toast.info('Restored unsaved changes from your last session — saving now…', { duration: 5000 });
            // Mark edited so the auto-save effect fires (also updates the server)
            userHasEdited.current = true;
          } else {
            // Draft is stale — server has newer data. Drop it.
            localStorage.removeItem(`dailyentry_draft_${dateStr}`);
          }
        }
      } catch { /* corrupted / unavailable localStorage — silent */ }

      // Apply closing numbers sent over from the Lotto Scanning page.
      // Payload: { date, ts, numbers: { denom: [3-digit tickets in slot order] } }
      try {
        const rawScan = localStorage.getItem('lotto_scan_closing');
        if (rawScan) {
          const payload = JSON.parse(rawScan);
          const fresh = payload && Number(payload.ts) > Date.now() - 6 * 3600 * 1000;
          if (fresh && payload.date === dateStr && payload.numbers) {
            setRunningNumbers(prev => {
              const merged = { ...prev };
              Object.keys(payload.numbers).forEach(d => {
                if (!merged[d]) return;
                const arr = [...merged[d]];
                (payload.numbers[d] || []).slice(0, MAX_SLOTS).forEach((t, i) => {
                  arr[i] = String(t || '').replace(/[^0-9]/g, '').slice(0, 3);
                });
                merged[d] = arr;
              });
              return merged;
            });
            userHasEdited.current = true;
            toast.success('Applied scanned closing numbers from Lotto Scanning', { duration: 5000 });
            localStorage.removeItem('lotto_scan_closing');
          } else if (!fresh) {
            localStorage.removeItem('lotto_scan_closing');
          }
        }
      } catch { /* malformed payload — ignore */ }

    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching entry:", error);
      if (seq !== fetchSeqRef.current) return;
      const newRunning = {};
      const newYesterday = {};
      const newInventory = {};
      DENOMINATIONS.forEach(d => {
        newRunning[d] = Array(MAX_SLOTS).fill('');
        newYesterday[d] = Array(MAX_SLOTS).fill('');
        newInventory[d] = { delivery: '', ticketAdded: '', ticketReturned: '', lvm: '', lvm2: '', endingInventory: '', registerSale: '' };
      });
      setRunningNumbers(newRunning);
      setYesterdayNumbers(newYesterday);
      setYesterdayInventory({});
      setIsFirstDay(false);
      setInventory(newInventory);
      const emptyPQ = {};
      DENOMINATIONS.forEach(d => { emptyPQ[d] = Array(MAX_SLOTS).fill(''); });
      setPackQuantities(emptyPQ);
      setYesterdayPackQuantities(emptyPQ);
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        // Wait one tick so React commits the state from fetchEntry BEFORE we
        // arm undo/redo — otherwise the initial fetched state itself would be
        // recorded as the first undoable change.
        setTimeout(() => { isInitialLoad.current = false; }, 0);
      }
    }
  };

  const stripMinus = (v) => v.replace(/-/g, '');
  const cleanNum = (v) => v.replace(/new/gi, '000').replace(/[^0-9]/g, '');
  const cleanNumSigned = (v) => v.replace(/[^0-9-]/g, '').replace(/(?!^)-/g, '');

  const cleanNumDecimal = (v) => v.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  const cleanNumDecimalSigned = (v) => v.replace(/[^0-9.\-]/g, '').replace(/(?!^)-/g, '').replace(/(\..*)\./g, '$1');
  // Strip the decimal portion only when it's all zeros (e.g. "20.00" → "20",
  // "20.0" → "20"). Real cents like "20.50" or "20.99" are preserved as-is.
  // Used on PASTE only — typing is unaffected.
  const stripTrailingZeros = (v) => {
    if (!v) return v;
    const m = v.match(/^(-?\d+)\.(\d+)$/);
    if (!m) return v;
    return /^0+$/.test(m[2]) ? m[1] : v;
  };

  // Display number: drop trailing .00, keep other decimals
  const fmtNum = (n) => {
    if (n === 0) return '0';
    const s = parseFloat(n.toFixed(2)).toString();
    return s;
  };

  // Render a number: negative → red bold (abs) in parentheses
  const renderNum = (n, extraClass = '') => {
    const val = parseFloat(n) || 0;
    if (val < 0) {
      return <span className={`text-red-600 font-extrabold ${extraClass}`}>({fmtNum(Math.abs(val))})</span>;
    }
    return <span className={extraClass}>{fmtNum(val)}</span>;
  };
  const formatCents = (raw, allowMinus = false) => {
    if (raw == null) return '';
    const s = String(raw);
    const isNeg = allowMinus && (s.startsWith('-') || (s.startsWith('(') && s.includes(')')));
    const sign = isNeg ? '-' : '';
    // Keep only digits and the first decimal point
    const cleaned = s.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    if (!cleaned) return '';
    const dotIdx = cleaned.indexOf('.');
    if (dotIdx === -1) {
      // No decimal — keep digits as-is
      return sign + cleaned;
    }
    const whole = cleaned.slice(0, dotIdx) || '0';
    const frac = cleaned.slice(dotIdx + 1);
    // If the fractional part is all zeros (or empty), strip the decimal
    // ("100.00" → "100"). Otherwise keep cents intact ("100.50" stays).
    if (frac === '' || /^0+$/.test(frac)) return sign + whole;
    return sign + whole + '.' + frac;
  };

  // Validate a single running-number cell against the denomination's pack
  // size. Returns the value if OK, or `null` to indicate "out of range".
  // - '' or '000' are always allowed ('' = empty; '000' = full new pack)
  // - 1-2 digit values are allowed mid-typing (e.g. "29" before becoming "299")
  // - Otherwise the integer value must be < PACK_SIZES[denom]
  const validateRunningValue = (denom, val) => {
    if (val === '' || val === '000') return val;
    if (val.length < 3) return val; // allow short partial inputs
    const num = parseInt(val, 10);
    if (isNaN(num)) return '';
    const max = (PACK_SIZES[denom] || 30) - 1;
    if (num > max) return null;
    return val;
  };

  // Toast over-limit warning at most once per second to avoid spamming when
  // pasting a large grid containing many out-of-range cells.
  const overLimitToastAt = useRef(0);
  const warnOverLimit = (denom) => {
    const now = Date.now();
    if (now - overLimitToastAt.current < 1000) return;
    overLimitToastAt.current = now;
    const max = (PACK_SIZES[denom] || 30) - 1;
    toast.error(`$${denom}: value must be 0-${max} (or 000 for a new pack)`);
  };

  const updateRunningNumber = (denom, slotIdx, value) => {
    const cleaned = cleanNum(value);
    const validated = validateRunningValue(denom, cleaned);
    if (validated === null) {
      warnOverLimit(denom);
      return; // reject the change; cell keeps its previous value
    }
    saveUndoPoint();
    markEdited();
    setRunningNumbers(prev => {
      const newArr = [...prev[denom]];
      newArr[slotIdx] = validated;
      return { ...prev, [denom]: newArr };
    });
  };

  // Convention: '' or '0' = no value / empty slot. '000' = full new pack.
  // Normalize on blur so users can still type 0 → 00 → 000 mid-keystroke.
  const normalizeRunningOnBlur = (denom, slotIdx) => {
    const cur = runningNumbers[denom]?.[slotIdx];
    if (cur === '0') {
      markEdited();
      setRunningNumbers(prev => {
        const newArr = [...(prev[denom] || [])];
        newArr[slotIdx] = '';
        return { ...prev, [denom]: newArr };
      });
    }
  };

  const updatePackQuantity = (denom, slotIdx, value) => {
    saveUndoPoint();
    markEdited();
    setPackQuantities(prev => {
      const newArr = [...(prev[denom] || Array(MAX_SLOTS).fill(''))];
      newArr[slotIdx] = cleanNum(value);
      return { ...prev, [denom]: newArr };
    });
  };

  const showQtyCell = (denom, slotIdx) => {
    const key = `${denom}-${slotIdx}`;
    setVisibleQtyCells(prev => ({ ...prev, [key]: true }));
    if (qtyTimers.current[key]) clearTimeout(qtyTimers.current[key]);
    qtyTimers.current[key] = setTimeout(() => {
      setVisibleQtyCells(prev => ({ ...prev, [key]: false }));
    }, 3000);
  };

  // Apply AI-scanned worksheet values (Closing grid + Lottery Data) into the
  // form state. User reviews in the dialog first and still saves normally.
  const applySheetScan = ({ closing, lottery_data }) => {
    saveUndoPoint();
    markEdited();
    setRunningNumbers(prev => {
      const merged = { ...prev };
      Object.keys(closing || {}).forEach(d => {
        if (!merged[d]) return;
        const arr = [...merged[d]];
        (closing[d] || []).slice(0, MAX_SLOTS).forEach((t, i) => {
          const v = String(t || '').replace(/\D/g, '').slice(0, 3);
          if (v) arr[i] = v;
        });
        merged[d] = arr;
      });
      return merged;
    });
    setInventory(prev => {
      const next = { ...prev };
      const fieldMap = { delivery: 'delivery', tkt_add: 'ticketAdded', return: 'ticketReturned', lvm1: 'lvm', lvm2: 'lvm2', ending: 'endingInventory' };
      Object.keys(lottery_data || {}).forEach(d => {
        if (!next[d]) return;
        const row = lottery_data[d] || {};
        const cur = { ...next[d] };
        Object.entries(fieldMap).forEach(([src, dst]) => {
          const v = String(row[src] ?? '').replace(/\D/g, '');
          if (v !== '') cur[dst] = v;
        });
        next[d] = cur;
      });
      return next;
    });
    toast.success('Scanned values filled in — review the grid, then Save');
  };

  const updateInventory = (denom, field, value) => {    saveUndoPoint();
    markEdited();
    // Register$ allows decimals (e.g. 100.00); other inventory fields stay integer
    const cleaned = field === 'registerSale' ? cleanNumDecimal(value) : cleanNum(value);
    setInventory(prev => ({
      ...prev,
      [denom]: { ...prev[denom], [field]: cleaned }
    }));
  };

  const updateMachine = (row, machine, value) => {
    saveUndoPoint();
    markEdited();
    const cleaned = row === 'onlineTotal' ? cleanNumDecimalSigned(value) : cleanNumDecimal(value);
    setMachineData(prev => ({
      ...prev,
      [row]: { ...prev[row], [machine]: cleaned }
    }));
  };

  const updateSalesComparison = (row, field, value) => {
    saveUndoPoint();
    markEdited();
    setSalesComparison(prev => ({
      ...prev,
      [row]: { ...prev[row], [field]: cleanNumDecimal(value) }
    }));
  };

  // How many rows to show per denomination (last filled + 1, min 1)
  const getVisibleCount = (denom) => {
    const arr = runningNumbers[denom] || [];
    let lastFilled = -1;
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i] !== '' && arr[i] !== undefined) { lastFilled = i; break; }
    }
    // Also check yesterday for pre-filled context
    const yArr = yesterdayNumbers[denom] || [];
    let lastYesterday = -1;
    for (let i = yArr.length - 1; i >= 0; i--) {
      if (yArr[i] !== '' && yArr[i] !== undefined) { lastYesterday = i; break; }
    }
    const expanded = expandedRows[denom] || 0;
    return Math.min(MAX_SLOTS, Math.max(1, lastFilled + 2, lastYesterday + 2, expanded));
  };

  // Double-click blank cell to expand and show next row
  const handleDoubleClick = (denom, slotIdx) => {
    const val = runningNumbers[denom]?.[slotIdx] || '';
    if (val === '') {
      setExpandedRows(prev => ({
        ...prev,
        [denom]: Math.max(prev[denom] || 0, slotIdx + 2)
      }));
      // Focus the next cell
      setTimeout(() => {
        const next = document.querySelector(`[data-grid-row="${slotIdx + 1}"][data-grid-col="${denom}"]`);
        if (next) next.focus();
      }, 50);
    }
  };

  // Multi-cell selection for copy/paste
  const [selectedCells, setSelectedCells] = useState([]); // [{denom, slotIdx}]
  const selectionAnchor = useRef(null); // first cell of shift-click range

  const handleCellClick = (denom, slotIdx, e) => {
    if (e.shiftKey && selectionAnchor.current && selectionAnchor.current.denom === denom) {
      // Shift+Click: select range in same column
      const start = Math.min(selectionAnchor.current.slotIdx, slotIdx);
      const end = Math.max(selectionAnchor.current.slotIdx, slotIdx);
      const range = [];
      for (let i = start; i <= end; i++) {
        range.push({ denom, slotIdx: i });
      }
      setSelectedCells(range);
    } else {
      // Normal click: set anchor, select single cell
      selectionAnchor.current = { denom, slotIdx };
      setSelectedCells([{ denom, slotIdx }]);
    }
  };

  const isCellSelected = (denom, slotIdx) => {
    return selectedCells.some(c => c.denom === denom && c.slotIdx === slotIdx);
  };

  // Enter key moves cursor down in same column
  const handleKeyDown = (e, denom, slotIdx) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextIdx = slotIdx + 1;
      if (nextIdx < MAX_SLOTS) {
        setTimeout(() => {
          const next = document.querySelector(`[data-grid-row="${nextIdx}"][data-grid-col="${denom}"]`);
          if (next) next.focus();
        }, 50);
      }
    }
    // Ctrl+C / Cmd+C: copy selected cells
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      if (selectedCells.length > 0) {
        e.preventDefault();
        const sorted = [...selectedCells].sort((a, b) => a.slotIdx - b.slotIdx);
        const values = sorted.map(c => runningNumbers[c.denom]?.[c.slotIdx] || '').join('\n');
        navigator.clipboard.writeText(values).then(() => {
          toast.success(`Copied ${sorted.length} cell${sorted.length > 1 ? 's' : ''}`);
        }).catch(() => {
          // Fallback: copy via textarea
          const ta = document.createElement('textarea');
          ta.value = values;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          toast.success(`Copied ${sorted.length} cell${sorted.length > 1 ? 's' : ''}`);
        });
      }
    }
    // Escape clears selection
    if (e.key === 'Escape') {
      setSelectedCells([]);
      selectionAnchor.current = null;
    }
  };

  // Handle paste event on running number cells — supports multi-row AND multi-column
  const handlePaste = (e, denom, slotIdx) => {
    const text = e.clipboardData?.getData('text');
    if (!text) return;
    markEdited();
    // Parse clipboard as 2D grid: rows split by newlines, columns by tabs
    // Normalize '0' → '' (empty slot). '000' is preserved (= full new pack marker).
    const normCell = (v) => {
      const c = cleanNum(v.trim());
      return c === '0' ? '' : c;
    };
    const rows = text.split(/[\r\n]+/).filter(r => r.trim() !== '');
    const grid = rows.map(r => r.split('\t').map(normCell));
    const numCols = Math.max(...grid.map(r => r.length));
    // Single value — clean and set directly
    if (rows.length <= 1 && numCols <= 1) {
      e.preventDefault();
      updateRunningNumber(denom, slotIdx, grid[0]?.[0] || '');
      return;
    }
    e.preventDefault();
    saveUndoPoint();
    const startDenomIdx = DENOMINATIONS.indexOf(denom);
    let overLimitCount = 0;
    setRunningNumbers(prev => {
      const newData = { ...prev };
      for (let rowOffset = 0; rowOffset < grid.length; rowOffset++) {
        for (let colOffset = 0; colOffset < grid[rowOffset].length; colOffset++) {
          const targetDenomIdx = startDenomIdx + colOffset;
          const targetSlot = slotIdx + rowOffset;
          if (targetDenomIdx >= DENOMINATIONS.length || targetSlot >= MAX_SLOTS) continue;
          const targetDenom = DENOMINATIONS[targetDenomIdx];
          const raw = grid[rowOffset][colOffset];
          const validated = validateRunningValue(targetDenom, raw);
          const arr = [...(newData[targetDenom] || [])];
          if (validated === null) {
            // Drop over-limit values silently; we'll toast once at the end
            arr[targetSlot] = '';
            overLimitCount++;
          } else {
            arr[targetSlot] = validated;
          }
          newData[targetDenom] = arr;
        }
      }
      return newData;
    });
    // Expand rows if needed for the last pasted row
    const lastPasteRow = slotIdx + grid.length - 1;
    const endDenomIdx = Math.min(startDenomIdx + numCols - 1, DENOMINATIONS.length - 1);
    setExpandedRows(prev => {
      const next = { ...prev };
      for (let ci = startDenomIdx; ci <= endDenomIdx; ci++) {
        const d = DENOMINATIONS[ci];
        next[d] = Math.max(next[d] || 0, lastPasteRow + 2);
      }
      return next;
    });
    toast.success(`Pasted ${grid.length} row${grid.length > 1 ? 's' : ''} × ${numCols} col${numCols > 1 ? 's' : ''}`);
    if (overLimitCount > 0) {
      toast.error(`${overLimitCount} cell${overLimitCount > 1 ? 's were' : ' was'} over the pack-size limit and skipped`);
    }
  };

  // Universal paste for inventory inputs (multi-row: paste down across denominations)
  const handleInventoryPaste = (e, denom, field) => {
    const text = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text');
    if (!text) return;
    e.preventDefault();
    e.stopPropagation();
    const rows = text.split(/[\r\n]+/).filter(r => r.trim() !== '');
    // Register$ allows decimals; others stay integer
    const grid = rows.map(r => r.split('\t'));
    saveUndoPoint();
    markEdited();
    const INV_FIELDS = ['delivery', 'ticketAdded', 'ticketReturned', 'lvm', 'lvm2', 'endingInventory', 'registerSale'];
    const startDenomIdx = DENOMINATIONS.indexOf(denom);
    const startFieldIdx = INV_FIELDS.indexOf(field);
    setInventory(prev => {
      const newInv = {};
      // Deep copy all existing data
      for (const key of Object.keys(prev)) {
        newInv[key] = { ...prev[key] };
      }
      for (let rowOffset = 0; rowOffset < grid.length; rowOffset++) {
        for (let colOffset = 0; colOffset < grid[rowOffset].length; colOffset++) {
          const targetDenomIdx = startDenomIdx + rowOffset;
          const targetFieldIdx = startFieldIdx + colOffset;
          if (targetDenomIdx >= DENOMINATIONS.length || targetFieldIdx >= INV_FIELDS.length) continue;
          const targetDenom = DENOMINATIONS[targetDenomIdx];
          const targetField = INV_FIELDS[targetFieldIdx];
          if (!newInv[targetDenom]) newInv[targetDenom] = {};
          const raw = grid[rowOffset][colOffset].trim();
          newInv[targetDenom][targetField] = targetField === 'registerSale' ? stripTrailingZeros(cleanNumDecimal(raw)) : cleanNum(raw);
        }
      }
      return newInv;
    });
    if (rows.length > 1 || grid[0]?.length > 1) {
      toast.success(`Pasted ${grid.length} row${grid.length > 1 ? 's' : ''} into inventory`);
    }
  };

  // Universal paste for machine data inputs
  const handleMachinePaste = (e, row, col) => {
    const text = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text');
    if (!text) return;
    e.preventDefault();
    e.stopPropagation();
    const rows = text.split(/[\r\n]+/).filter(r => r.trim() !== '');
    const grid = rows.map(r => r.split('\t').map(v => v.trim()));
    saveUndoPoint();
    markEdited();
    const MACHINE_COLS = ['machine1', 'machine2'];
    const startRowIdx = MACHINE_ROWS.indexOf(row);
    const startColIdx = MACHINE_COLS.indexOf(col);
    setMachineData(prev => {
      const newData = {};
      for (const key of Object.keys(prev)) {
        newData[key] = { ...prev[key] };
      }
      for (let rowOffset = 0; rowOffset < grid.length; rowOffset++) {
        for (let colOffset = 0; colOffset < grid[rowOffset].length; colOffset++) {
          const targetRowIdx = startRowIdx + rowOffset;
          const targetColIdx = startColIdx + colOffset;
          if (targetRowIdx >= MACHINE_ROWS.length || targetColIdx >= MACHINE_COLS.length) continue;
          const targetRow = MACHINE_ROWS[targetRowIdx];
          const targetCol = MACHINE_COLS[targetColIdx];
          if (!newData[targetRow]) newData[targetRow] = {};
          const val = grid[rowOffset][colOffset];
          newData[targetRow][targetCol] = formatCents(val, targetRow === 'onlineTotal');
        }
      }
      return newData;
    });
    if (rows.length > 1 || grid[0]?.length > 1) {
      toast.success(`Pasted into Online Reports`);
    }
  };

  // Universal paste for sales comparison inputs
  const handleSalesPaste = (e, row) => {
    const text = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text');
    if (!text) return;
    e.preventDefault();
    e.stopPropagation();
    const rows = text.split(/[\r\n]+/).filter(r => r.trim() !== '');
    saveUndoPoint();
    markEdited();
    const startRowIdx = SALES_ROWS.indexOf(row);
    setSalesComparison(prev => {
      const newData = {};
      for (const key of Object.keys(prev)) {
        newData[key] = { ...prev[key] };
      }
      for (let i = 0; i < rows.length; i++) {
        const targetRowIdx = startRowIdx + i;
        if (targetRowIdx >= SALES_ROWS.length) break;
        const targetRow = SALES_ROWS[targetRowIdx];
        if (!newData[targetRow]) newData[targetRow] = {};
        newData[targetRow].register = formatCents(rows[i].trim());
      }
      return newData;
    });
    if (rows.length > 1) {
      toast.success(`Pasted ${rows.length} values into Sales Comparison`);
    }
  };

  // Enter key moves cursor down in inventory (same column, next denomination)
  const handleInventoryKeyDown = (e, denom, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const denomIdx = DENOMINATIONS.indexOf(denom);
      if (denomIdx < DENOMINATIONS.length - 1) {
        const nextDenom = DENOMINATIONS[denomIdx + 1];
        setTimeout(() => {
          const next = document.querySelector(`[data-inv-denom="${nextDenom}"][data-inv-field="${field}"]`);
          if (next) next.focus();
        }, 50);
      }
    }
  };

  // Get effective pack size for a slot (custom qty → yesterday qty → default)
  const getSlotPackSize = (denom, slotIdx) => {
    const curPQ = packQuantities[denom]?.[slotIdx];
    const qty = parseInt(curPQ) || 0;
    if (qty > 0) return qty;
    // If user explicitly cleared it (typed then backspaced), don't fall through
    // Only use yesterday's if curPQ was never set (empty from init)
    const yQty = parseInt(yesterdayPackQuantities[denom]?.[slotIdx]) || 0;
    if (yQty > 0) return yQty;
    return PACK_SIZES[denom]; // default from admin settings
  };

  // Calculate sales for a slot
  const getSlotSales = (denom, slotIdx) => {
    const todayVal = runningNumbers[denom]?.[slotIdx];
    const yesterdayVal = yesterdayNumbers[denom]?.[slotIdx];
    const packSize = getSlotPackSize(denom, slotIdx);
    const yesterday = parseInt(yesterdayVal) || 0;
    
    // Today is blank but yesterday had a value = pack sold out
    if (todayVal === '' || todayVal === undefined) {
      if (yesterdayVal !== '' && yesterdayVal !== undefined && packSize > 0) {
        return packSize - yesterday;
      }
      return 0;
    }
    
    const today = parseInt(todayVal);
    if (today >= yesterday) {
      return today - yesterday;
    } else {
      return (packSize - yesterday) + today;
    }
  };

  // Get total sales for denomination
  const getDenomSales = (denom) => {
    let total = 0;
    for (let i = 0; i < MAX_SLOTS; i++) {
      total += getSlotSales(denom, i);
    }
    return total;
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Voice dictation
  //
  // Single-button workflow: user clicks the mic button, then clicks a grid
  // cell, then speaks. The browser's Web Speech API finalizes each utterance
  // after a brief pause (~600-1000ms) — we take that final transcript, parse
  // it, write digits into the focused cell, and advance focus by one row.
  // Voice commands: "next" / "next game" → next denomination's first cell.
  // "ten" / "twenty" / etc. → jump to that denomination's first cell.
  // "clear" → empty current cell. "stop" → end dictation.
  // ──────────────────────────────────────────────────────────────────────────
  const lastFocusedCellRef = useRef(null); // { denom, slotIdx }
  const [voiceTargetCell, setVoiceTargetCell] = useState(null); // for UI ring
  // Ref bridge so handleVoiceTranscript can call .stop() (defined by the hook below).
  const voiceCtrlRef = useRef({ stop: null });
  // Ref bridge for handleSave (declared further down).
  const saveActionRef = useRef(null);
  // Rolling log of recent voice commands (most-recent first, max 5).
  const [voiceLog, setVoiceLog] = useState([]);
  const pushVoiceLog = useCallback((heard, action) => {
    setVoiceLog(prev => [{ heard, action, t: Date.now() }, ...prev].slice(0, 5));
  }, []);

  const focusGridCell = useCallback((denom, slotIdx) => {
    // Expand row count if needed so the target cell is rendered.
    setExpandedRows(prev => ({
      ...prev,
      [denom]: Math.max(prev[denom] || 0, slotIdx + 1),
    }));
    // Wait a tick so React renders the newly expanded row, then focus.
    setTimeout(() => {
      const el = document.querySelector(
        `[data-grid-row="${slotIdx}"][data-grid-col="${denom}"]`
      );
      if (el) {
        el.focus();
        try { el.select && el.select(); } catch (_) { /* ignore */ }
        lastFocusedCellRef.current = { denom, slotIdx };
        setVoiceTargetCell({ denom, slotIdx });
      }
    }, 30);
  }, []);

  const advanceFocusInColumn = useCallback(() => {
    const cur = lastFocusedCellRef.current;
    if (!cur) return;
    const nextIdx = cur.slotIdx + 1;
    if (nextIdx >= MAX_SLOTS) return;
    focusGridCell(cur.denom, nextIdx);
  }, [focusGridCell]);

  const jumpToNextDenomination = useCallback(() => {
    const cur = lastFocusedCellRef.current;
    const curDenom = cur?.denom ?? DENOMINATIONS[0];
    const idx = DENOMINATIONS.indexOf(curDenom);
    const nextDenom = idx === -1 || idx >= DENOMINATIONS.length - 1
      ? DENOMINATIONS[0]
      : DENOMINATIONS[idx + 1];
    focusGridCell(nextDenom, 0);
  }, [focusGridCell]);

  const handleVoiceTranscript = useCallback((rawText) => {
    const cmd = parseVoiceCommand(rawText, { denominations: DENOMINATIONS });
    if (cmd.type === "noop") return;
    if (cmd.type === "unknown") {
      pushVoiceLog(rawText, "didn't understand");
      return;
    }
    if (cmd.type === "stop") {
      pushVoiceLog(rawText, "stop");
      voiceCtrlRef.current?.stop?.();
      toast.success("Voice dictation stopped");
      return;
    }
    if (cmd.type === "save") {
      pushVoiceLog(rawText, "save");
      const fn = saveActionRef.current;
      if (fn) { fn(); toast.success("Saving…"); }
      return;
    }
    if (cmd.type === "next") {
      pushVoiceLog(rawText, "→ next game");
      jumpToNextDenomination();
      return;
    }
    if (cmd.type === "jump") {
      pushVoiceLog(rawText, `→ $${cmd.denom}`);
      focusGridCell(cmd.denom, 0);
      return;
    }
    if (cmd.type === "clear") {
      const cur = lastFocusedCellRef.current;
      pushVoiceLog(rawText, cur ? `clear $${cur.denom} row ${cur.slotIdx + 1}` : "clear (no cell)");
      if (!cur) return;
      updateRunningNumber(cur.denom, cur.slotIdx, "");
      return;
    }
    if (cmd.type === "digits") {
      const cur = lastFocusedCellRef.current;
      if (!cur) {
        pushVoiceLog(rawText, `${cmd.digits} (no cell focused)`);
        toast.error("Click a running-number cell first, then speak.", { id: "voice-no-cell" });
        return;
      }
      pushVoiceLog(rawText, `${cmd.digits} → $${cur.denom} row ${cur.slotIdx + 1}`);
      updateRunningNumber(cur.denom, cur.slotIdx, cmd.digits);
      // Advance focus to next slot in the same column.
      setTimeout(() => advanceFocusInColumn(), 50);
    }
  }, [focusGridCell, jumpToNextDenomination, advanceFocusInColumn, pushVoiceLog]);

  const voice = useVoiceDictation({
    onTranscript: handleVoiceTranscript,
    commitDelayMs: 500,
    onError: (e) => {
      if (!e) return;
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Microphone permission denied. Allow it in your browser, then try again.");
      } else if (e.error === "no-speech") {
        // Silent. Browser fires this after a long silence — recognizer auto-restarts.
      } else if (e.error === "audio-capture") {
        toast.error("No microphone detected.");
      } else if (e.error === "start-failed") {
        toast.error("Could not start voice dictation: " + (e.message || ""));
      }
    },
  });

  // Keep voiceCtrlRef in sync (so handleVoiceTranscript can call stop()).
  useEffect(() => {
    voiceCtrlRef.current.stop = voice.stop;
  }, [voice.stop]);

  const toggleVoice = () => {
    if (voice.isListening) {
      voice.stop();
      toast.success("Voice dictation stopped");
    } else {
      const ok = voice.start();
      if (ok) {
        toast.success("Listening — click a cell, then speak digits", { duration: 4000 });
      }
    }
  };

  // Get actual sale dollars for a denomination
  // Uses per-slot pack quantities for accurate calculation
  const getDenomActualSaleDollars = (denom, withBreakdown = false) => {
    const openingPacks = parseInt(yesterdayInventory[denom]?.endingInventory) || 0;
    const deliveryPacks = parseInt(inventory[denom]?.delivery) || 0;
    const ticketAdded = parseInt(inventory[denom]?.ticketAdded) || 0;
    const ticketReturned = parseInt(inventory[denom]?.ticketReturned) || 0;
    const lvm1Packs = parseInt(inventory[denom]?.lvm) || 0;
    const lvm2Packs = parseInt(inventory[denom]?.lvm2) || 0;
    const lvmPacks = lvm1Packs + lvm2Packs;
    const endingPacks = parseInt(inventory[denom]?.endingInventory) || 0;

    // Calculate per-slot values using actual qty from tiny cells
    const tArr = runningNumbers[denom] || [];
    const yArr = yesterdayNumbers[denom] || [];
    
    // Sum new pack values across all active slots (each pack uses its own qty)
    let newPackValueTotal = 0;
    let newPacks = 0;
    for (let i = 0; i < tArr.length; i++) {
      const hasToday = tArr[i] !== '' && tArr[i] !== undefined && tArr[i] !== null;
      const hasYest = yArr[i] !== '' && yArr[i] !== undefined && yArr[i] !== null;
      const slotPackSize = getSlotPackSize(denom, i);
      if (hasToday && !hasYest) {
        // New pack
        newPacks++;
        newPackValueTotal += slotPackSize * denom;
      } else if (hasToday && hasYest) {
        const todayVal = parseInt(tArr[i]) || 0;
        const yestVal = parseInt(yArr[i]) || 0;
        if (todayVal < yestVal) {
          // Rollover - new pack
          newPacks++;
          newPackValueTotal += slotPackSize * denom;
        }
      }
    }

    // Inventory math is about WHOLE PACKS at the standard (default) size for
    // this denomination — not per-slot custom qty. Custom slot qty only
    // affects running-number math (already captured in newPackValueTotal).
    // PACK_SIZES is the snapshotted (historical) or current default pack size.
    const standardPackSize = PACK_SIZES[denom];
    const packValue = standardPackSize * denom;

    const inventorySale = (openingPacks + deliveryPacks - lvmPacks - endingPacks) * packValue;
    const ticketAdjustment = (ticketAdded - ticketReturned) * denom;
    const rnSaleTickets = getDenomSales(denom);
    const runningNumberSale = rnSaleTickets * denom;
    const total = inventorySale + ticketAdjustment + (runningNumberSale - newPackValueTotal);

    if (withBreakdown) {
      return {
        total,
        breakdown: `Open:${openingPacks} Del:${deliveryPacks} LVM1:${lvm1Packs} LVM2:${lvm2Packs} End:${endingPacks} PackSize:${standardPackSize} PV:$${packValue}\nInvSale:(${openingPacks}+${deliveryPacks}-${lvmPacks}-${endingPacks})×$${packValue}=$${inventorySale}\nTkt+:${ticketAdded} Tkt-:${ticketReturned} TktAdj:$${ticketAdjustment}\nRN Tickets:${rnSaleTickets} RNSale:$${runningNumberSale}\nNewPacks:${newPacks} NewPV:$${newPackValueTotal}\nTotal:$${inventorySale}+$${ticketAdjustment}+($${runningNumberSale}-$${newPackValueTotal})=$${total}`
      };
    }
    return total;
  };

  // Computed values for Sales Comparison "Actual" column
  const getComputedSalesActual = () => {
    const instantSale = DENOMINATIONS.reduce((sum, d) => sum + getDenomActualSaleDollars(d), 0);
    const onlinePaysTotal = (parseFloat(machineData.onlinePays?.machine1) || 0) + (parseFloat(machineData.onlinePays?.machine2) || 0);
    const onlineTotalTotal = (parseFloat(machineData.onlineTotal?.machine1) || 0) + (parseFloat(machineData.onlineTotal?.machine2) || 0);
    const onlineSale = onlinePaysTotal + onlineTotalTotal;
    const instantPaidOut = (parseFloat(machineData.instantGrandTotal?.machine1) || 0) + (parseFloat(machineData.instantGrandTotal?.machine2) || 0);
    const onlinePaidOut = onlinePaysTotal;
    return { instantSale, onlineSale, instantPaidOut, onlinePaidOut };
  };

  const handleSave = useCallback(async (numbers, inv, machines, salesComp) => {
    // Serialize saves: if a POST is already in flight, mark that another
    // save is needed and return. When the current save completes, the
    // finally-block will re-fire with the LATEST state from latestStateRef,
    // so nothing is ever left unsaved and older responses cannot clobber
    // newer ones (classic AJAX race condition).
    if (saveInFlightRef.current) {
      pendingSaveRef.current = true;
      return;
    }
    saveInFlightRef.current = true;
    setSaveStatus('saving');
    try {
      // On the user's first-ever day, zero out sales to prevent bad data in totals
      const sales = isFirstDay
        ? DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d]: 0 }), {})
        : DENOMINATIONS.reduce((acc, d) => {
            let total = 0;
            for (let i = 0; i < MAX_SLOTS; i++) {
              const todayVal = numbers[d]?.[i];
              const yesterdayVal = yesterdayNumbers[d]?.[i];
              const yesterday = parseInt(yesterdayVal) || 0;
              // Blank today — pack finished if yesterday had value and pack size known
              if (!todayVal || todayVal === '') {
                if (yesterdayVal && yesterdayVal !== '') {
                  const slotPack = getSlotPackSize(d, i);
                  if (slotPack > 0) total += slotPack - yesterday;
                }
                continue;
              }
              const today = parseInt(todayVal);
              const slotPack = getSlotPackSize(d, i);
              total += today >= yesterday ? today - yesterday : (slotPack - yesterday) + today;
            }
            return { ...acc, [d]: total };
          }, {});
      // Compute actual total sales matching the header display
      const actuals = getComputedSalesActual();
      const computedTotalSales = isFirstDay ? 0 : actuals.instantSale + actuals.onlineSale;
      const computedTotalPaidOuts = isFirstDay ? 0 : actuals.instantPaidOut + actuals.onlinePaidOut;

      await axios.post(`${API}/entry`, {
        date: dateStr,
        runningNumbers: numbers,
        packQuantities,
        inventory: inv,
        machineData: machines,
        salesComparison: salesComp,
        sales,
        computedTotalSales,
        computedTotalPaidOuts
      });
      setSaveStatus('saved');
      lastSavedAtRef.current = Date.now();
      // Server confirmed — safe to drop the local draft.
      try {
        if (dateStr) localStorage.removeItem(`dailyentry_draft_${dateStr}`);
      } catch { /* localStorage may be unavailable in private mode */ }
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error saving:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || "Your data entry is paused. Contact admin.";
        setIsPaused(true);
        setPauseMessage(detail);
        toast.error(detail);
      } else {
        const serverMsg = error.response?.data?.detail || error.message || "Unknown error";
        toast.error(`Failed to save: ${serverMsg}`);
      }
      setSaveStatus('');
    } finally {
      saveInFlightRef.current = false;
      // If someone requested another save while we were running, fire it
      // now with the latest state (never the stale args we were called with).
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        const s = latestStateRef.current;
        // Non-blocking: schedule on next tick so we don't grow the stack
        setTimeout(() => {
          handleSave(s.runningNumbers, s.inventory, s.machineData, s.salesComparison);
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, yesterdayNumbers, isFirstDay, packQuantities]);

  const userHasEdited = useRef(false);

  // Mark that user has made an edit (called from all update functions)
  const markEdited = () => { userHasEdited.current = true; };

  // Keep latestStateRef in sync so flush-on-hide and pending-save re-fires
  // always see the newest values, not a stale closure snapshot.
  useEffect(() => {
    latestStateRef.current = {
      runningNumbers, inventory, machineData, salesComparison, packQuantities,
    };
  }, [runningNumbers, inventory, machineData, salesComparison, packQuantities]);

  // Persist an in-progress draft to localStorage on every edit. This is the
  // last-line-of-defense against tab suspension / crash / network loss —
  // fetchEntry restores it on the next load if it's newer than the server.
  useEffect(() => {
    if (isInitialLoad.current) return;
    if (!userHasEdited.current) return;
    if (!dateStr) return;
    try {
      const draft = {
        ts: Date.now(),
        date: dateStr,
        runningNumbers, inventory, machineData, salesComparison, packQuantities,
      };
      localStorage.setItem(`dailyentry_draft_${dateStr}`, JSON.stringify(draft));
    } catch { /* localStorage full or unavailable — silent */ }
  }, [dateStr, runningNumbers, inventory, machineData, salesComparison, packQuantities]);

  // Expose a stable save invoker for voice commands (saveActionRef declared above).
  useEffect(() => {
    saveActionRef.current = () => {
      handleSave(runningNumbers, inventory, machineData, salesComparison);
    };
  }, [handleSave, runningNumbers, inventory, machineData, salesComparison]);

  // Auto-save: trigger 800ms after any change, ONLY if user has actually edited.
  // 800ms (down from 1500ms) means the save is far more likely to fire before
  // the user minimizes / switches tabs — background tabs throttle setTimeout.
  useEffect(() => {
    if (!userHasEdited.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      handleSave(runningNumbers, inventory, machineData, salesComparison);
      userHasEdited.current = false;
    }, 800);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [runningNumbers, inventory, machineData, salesComparison, packQuantities, handleSave]);

  // Flush any pending save synchronously when the tab becomes hidden /
  // unloaded. Uses `fetch` with `keepalive: true` so the request survives
  // tab suspension. This is the KEY fix for the "minimize → data disappears"
  // report: without this, background-tab throttling can indefinitely delay
  // the debounced setTimeout, and the user may return to a page whose
  // recent edits never reached the server.
  const flushSaveOnHide = useCallback(() => {
    // If nothing pending and no in-flight save, skip.
    if (!userHasEdited.current && !saveInFlightRef.current) return;
    if (!dateStr) return;
    // Cancel the debounced auto-save so we don't double-save
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }

    const snap = latestStateRef.current;
    const numbers = snap.runningNumbers || {};
    const inv = snap.inventory || {};
    const machines = snap.machineData || {};
    const salesComp = snap.salesComparison || {};
    const packQty = snap.packQuantities || {};

    // Recompute sales exactly like handleSave — need to match server contract
    const sales = isFirstDay
      ? DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d]: 0 }), {})
      : DENOMINATIONS.reduce((acc, d) => {
          let total = 0;
          for (let i = 0; i < MAX_SLOTS; i++) {
            const todayVal = numbers[d]?.[i];
            const yesterdayVal = yesterdayNumbers[d]?.[i];
            const yesterday = parseInt(yesterdayVal) || 0;
            if (!todayVal || todayVal === '') {
              if (yesterdayVal && yesterdayVal !== '') {
                const slotPack = getSlotPackSize(d, i);
                if (slotPack > 0) total += slotPack - yesterday;
              }
              continue;
            }
            const today = parseInt(todayVal);
            const slotPack = getSlotPackSize(d, i);
            total += today >= yesterday ? today - yesterday : (slotPack - yesterday) + today;
          }
          return { ...acc, [d]: total };
        }, {});
    const actuals = getComputedSalesActual();
    const computedTotalSales = isFirstDay ? 0 : actuals.instantSale + actuals.onlineSale;
    const computedTotalPaidOuts = isFirstDay ? 0 : actuals.instantPaidOut + actuals.onlinePaidOut;
    const payload = {
      date: dateStr,
      runningNumbers: numbers,
      packQuantities: packQty,
      inventory: inv,
      machineData: machines,
      salesComparison: salesComp,
      sales,
      computedTotalSales,
      computedTotalPaidOuts,
    };
    // fetch(...) with keepalive:true survives page unload / tab suspension.
    // Credentials 'include' sends the httpOnly session cookie.
    try {
      fetch(`${API}/entry`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* silent — nothing we can do if the tab is dying */ });
      userHasEdited.current = false;
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, yesterdayNumbers, isFirstDay]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushSaveOnHide();
    };
    const onPageHide = () => flushSaveOnHide();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, [flushSaveOnHide]);

  // Clear-this-page is available for any date — the confirm-email prompt
  // is the only guard (data is soft-deleted and recoverable for 30 days).

  const openClearDialog = () => {
    setClearConfirmText('');
    setClearDialogOpen(true);
  };

  const closeClearDialog = () => {
    setClearDialogOpen(false);
    setClearConfirmText('');
  };

  const handleClearPage = async () => {
    const requiredEmail = (authUser?.email || '').trim().toLowerCase();
    if (clearConfirmText.trim().toLowerCase() !== requiredEmail) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/entry/${dateStr}`);
      toast.success("Page cleared — recoverable from Admin → Recovery for 30 days");
      // Reset all state
      const newRunning = {};
      const newInventory = {};
      DENOMINATIONS.forEach(d => {
        newRunning[d] = Array(MAX_SLOTS).fill('');
        newInventory[d] = { delivery: '', ticketAdded: '', ticketReturned: '', lvm: '', lvm2: '', endingInventory: '', registerSale: '' };
      });
      setRunningNumbers(newRunning);
      setInventory(newInventory);
      setMachineData(() => {
        const md = {};
        MACHINE_ROWS.forEach(r => { md[r] = { machine1: '', machine2: '' }; });
        return md;
      });
      setSalesComparison(() => {
        const sc = {};
        SALES_ROWS.forEach(r => { sc[r] = { actual: '', register: '' }; });
        return sc;
      });
      const emptyPQ = {};
      DENOMINATIONS.forEach(d => { emptyPQ[d] = Array(MAX_SLOTS).fill(''); });
      setPackQuantities(emptyPQ);
      userHasEdited.current = false;
      closeClearDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear data");
    } finally {
      setClearing(false);
    }
  };


  // Warn when switching FROM a recent date TO a non-recent one (old or future)
  const [pendingDate, setPendingDate] = useState(null);
  const requestDateChange = (d) => {
    if (!d) return;
    if (isNotRecentDay(d) && !isNotRecentDay(selectedDate)) setPendingDate(d);
    else setSelectedDate(d);
  };
  const goToPrevDay = () => requestDateChange(subDays(selectedDate, 1));
  const goToNextDay = () => requestDateChange(addDays(selectedDate, 1));

  const grandTotal = DENOMINATIONS.reduce((sum, d) => sum + (getDenomSales(d) * d), 0);
  const summaryActuals = getComputedSalesActual();
  const totalSales = summaryActuals.instantSale + summaryActuals.onlineSale;
  const totalPaidOuts = summaryActuals.instantPaidOut + summaryActuals.onlinePaidOut;

  // Check if any data has been entered for today
  const hasData = (() => {
    // Check running numbers
    for (const d of DENOMINATIONS) {
      if (runningNumbers[d]?.some(v => v !== '' && v !== undefined && v !== null)) return true;
    }
    // Check inventory
    for (const d of DENOMINATIONS) {
      const inv = inventory[d];
      if (inv && Object.values(inv).some(v => v !== '' && v !== undefined && v !== null)) return true;
    }
    // Check machine data
    for (const r of MACHINE_ROWS) {
      const m = machineData[r];
      if (m && ((m.machine1 !== '' && m.machine1 !== undefined) || (m.machine2 !== '' && m.machine2 !== undefined))) return true;
    }
    // Check sales comparison
    for (const r of SALES_ROWS) {
      const s = salesComparison[r];
      if (s && ((s.register !== '' && s.register !== undefined) || (s.actual !== '' && s.actual !== undefined))) return true;
    }
    return false;
  })();

  // Hide all calculated values on first-ever day (no yesterday data) or when no data entered
  const showCalculations = hasData && !isFirstDay;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div {...FADE_IN} className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-mono text-2xl font-bold text-black">Daily Lottery</h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <p className="text-sm text-black/70">Total Sales: <span className="font-bold">{showCalculations ? renderNum(totalSales) : ''}</span></p>
            <p className="text-sm text-black/70">Total Paid Outs: <span className="font-bold">{showCalculations ? renderNum(totalPaidOuts) : ''}</span></p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={goToPrevDay} className="btn-3d">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="btn-3d w-[160px] font-mono text-sm">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(selectedDate, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => { if(d) { requestDateChange(d); setCalendarOpen(false); }}} />
            </PopoverContent>
          </Popover>
          
          <Button variant="outline" size="icon" onClick={goToNextDay} className="btn-3d">
            <ChevronRight className="w-4 h-4" />
          </Button>
          
          <Button variant="outline" size="sm" onClick={() => navigate(`/print/daily/${dateStr}`)} className="btn-3d">
            <Printer className="w-4 h-4" />
          </Button>

          <ExportButton section="daily" currentDate={dateStr} testIdPrefix="daily-export" />

          <SheetScanButton onApply={applySheetScan} />

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave(runningNumbers, inventory, machineData, salesComparison)}
            disabled={saveStatus === 'saving'}
            className="btn-3d"
            title="Save Now"
            data-testid="manual-save-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            <span className="text-xs">{saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save'}</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={openClearDialog}
            className="btn-3d text-red-400 hover:text-red-300"
            title="Clear this page's data"
            data-testid="clear-page-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            <span className="text-xs">Clear</span>
          </Button>
          
          <div className="w-px h-6 bg-border/50" />
          
          <Button variant="outline" size="icon" onClick={handleUndo} disabled={undoCount === 0} title="Undo" data-testid="undo-btn" className="btn-3d">
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={handleRedo} disabled={redoCount === 0} title="Redo" data-testid="redo-btn" className="btn-3d">
            <Redo2 className="w-4 h-4" />
          </Button>
          
          <div className="w-px h-6 bg-border/50" />
          
          <Button
            variant={swapMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setSwapMode(!swapMode); setSwapFirst(null); }}
            title="Swap yesterday's carry-forward values"
            data-testid="swap-btn"
            className={swapMode ? "bg-orange-600 hover:bg-orange-700 text-white" : "btn-3d"}
          >
            <ArrowUpDown className="w-4 h-4 mr-1" />
            <span className="text-xs">Swap</span>
          </Button>

          <div className="w-px h-6 bg-border/50" />

          <div
            className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-border/50 bg-card/40"
            title="Show the small pack-quantity boxes below each running number"
          >
            <Checkbox
              id="daily-show-qty-toggle"
              checked={showQtyCells}
              onCheckedChange={handleToggleShowQty}
              data-testid="daily-toggle-show-qty"
            />
            <Label
              htmlFor="daily-show-qty-toggle"
              className="font-mono text-[11px] cursor-pointer leading-none select-none"
            >
              Show pack qty cells
            </Label>
          </div>

          <VoiceDictationButton
            isSupported={voice.isSupported}
            isListening={voice.isListening}
            onToggle={toggleVoice}
          />
        </div>
      </div>

      {/* Running Numbers + Machine Block Row */}
      {voice.isListening && (
        <div className="bg-red-600/15 border border-red-500/50 rounded-lg px-4 py-2 flex items-center gap-3 animate-pulse" data-testid="voice-listening-banner">
          <span className="inline-flex h-3 w-3 rounded-full bg-red-500" />
          <span className="font-mono text-sm text-red-200">
            Listening… click a running-number cell, then speak. Say <span className="font-bold text-white">{'"next game"'}</span>, <span className="font-bold text-white">{'"twenty"'}</span>, <span className="font-bold text-white">{'"clear"'}</span>, <span className="font-bold text-white">{'"save"'}</span>, or <span className="font-bold text-white">{'"stop"'}</span>.
          </span>
          <Button variant="ghost" size="sm" onClick={toggleVoice} className="ml-auto text-red-200 hover:text-red-100">
            Stop
          </Button>
        </div>
      )}
      {swapMode && (
        <div className="bg-orange-600/20 border border-orange-500/50 rounded-lg px-4 py-2 flex items-center gap-3">
          <ArrowUpDown className="w-4 h-4 text-orange-400" />
          <span className="font-mono text-sm text-orange-300">
            {swapFirst ? `Row ${swapFirst.slotIdx + 1} selected in $${swapFirst.denom} — now click the second row to swap` : "Click a yesterday's value (small number above the cell) to select it, then click another in the same column to swap"}
          </span>
          <Button variant="ghost" size="sm" onClick={() => { setSwapMode(false); setSwapFirst(null); }} className="ml-auto text-orange-400 hover:text-orange-300">
            Cancel
          </Button>
        </div>
      )}
      {isPaused && (
        <div className="bg-red-600/20 border border-red-500/50 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="paused-banner">
          <PauseCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="font-mono text-sm text-red-300">{pauseMessage}</span>
        </div>
      )}
      {isFirstDay && (
        <div className="bg-emerald-800/40 border border-emerald-500/40 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="first-day-banner">
          <Info className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="font-mono text-sm text-emerald-200">
            <span className="font-bold text-emerald-100">First Day Setup</span> — Enter your current <span className="font-bold text-white">Running Numbers</span> and <span className="font-bold text-white">Ending Packs</span> to establish your starting point. Sales calculations will begin from your next entry.
          </div>
        </div>
      )}
      {dateStr !== format(new Date(), "yyyy-MM-dd") && (
        <div className="bg-amber-500/20 border-2 border-amber-500/70 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="not-today-banner">
          <span className="font-mono text-sm text-amber-200">
            <strong className="text-amber-100">Heads up:</strong> you're entering data for{" "}
            <strong className="text-white">{format(selectedDate, "EEE, MMM d, yyyy")}</strong> — not today.
          </span>
          <Button
            size="sm"
            onClick={() => setSelectedDate(new Date())}
            className="h-7 bg-amber-500 text-black hover:bg-amber-400 font-mono font-bold"
            data-testid="go-to-today-btn"
          >
            Go to Today
          </Button>
        </div>
      )}
      <DateWarningDialog
        pendingDate={pendingDate}
        onConfirm={() => { setSelectedDate(pendingDate); setPendingDate(null); }}
        onToday={() => { setSelectedDate(new Date()); setPendingDate(null); }}
        onCancel={() => setPendingDate(null)}
      />
      <div className="flex flex-col lg:flex-row gap-3 items-start">
      {/* Running Numbers Grid - Left column (tall) */}
      <Card className="border-0 shadow-lg flex-shrink-0 w-full lg:w-auto max-w-full overflow-hidden">
        <CardHeader className="py-2 px-3 flex flex-row items-center justify-between space-y-0 gap-2">
          <CardTitle className="font-mono text-sm">Running Numbers</CardTitle>
          <div className="flex items-center gap-2 flex-wrap text-[9px] font-mono text-muted-foreground" title="Pack sizes used for sales math on this date. Snapshotted when the entry was first saved.">
            <span className="font-bold text-primary/80">Pack sizes:</span>
            {DENOMINATIONS.map(d => (
              <span key={d} className="px-1 py-0.5 bg-secondary rounded" data-testid={`pack-size-display-${d}`}>
                ${d}=<span className="font-bold text-primary">{PACK_SIZES[d]}</span>
              </span>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-1">
          <div className="hidden sm:block overflow-x-auto">
            <table className="border-collapse mx-auto" style={{ borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th className="px-0.5 text-xs text-muted-foreground w-6">#</th>
                  {DENOMINATIONS.map(d => (
                    <th key={d} className="px-0.5 py-1 text-center">
                      <div className="th-pill-square" data-testid={`denom-header-${d}`}>
                        <span className="denom">${d}</span>
                        <span className="range">0-{PACK_SIZES[d] - 1}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.max(...DENOMINATIONS.map(d => getVisibleCount(d))) }).map((_, slotIdx) => (
                  <tr key={`slot-${slotIdx}`}>
                    <td className="px-0.5 text-xs text-muted-foreground text-center">{slotIdx + 1}</td>
                    {DENOMINATIONS.map(denom => {
                      if (slotIdx >= getVisibleCount(denom)) {
                        return <td key={denom} className="px-0.5" />;
                      }
                      const val = runningNumbers[denom]?.[slotIdx] || '';
                      const yesterday = yesterdayNumbers[denom]?.[slotIdx] || '';
                      const sales = getSlotSales(denom, slotIdx);
                      const isWrapped = val !== '' && parseInt(val) < parseInt(yesterday);
                      const slotPack = getSlotPackSize(denom, slotIdx);
                      const bothOverPack = val !== '' && yesterday !== '' && parseInt(val) > slotPack && parseInt(yesterday) > slotPack;
                      const yesterdayOverPack = yesterday !== '' && parseInt(yesterday) > slotPack;
                      const isSwapSelected = swapMode && swapFirst?.denom === denom && swapFirst?.slotIdx === slotIdx;
                      const isSwapTarget = swapMode && swapFirst && swapFirst.denom === denom && swapFirst.slotIdx !== slotIdx;
                      const isSelected = isCellSelected(denom, slotIdx);
                      
                      return (
                        <td key={denom} className="px-0.5 text-center">
                          <div className="relative inline-block">
                            {/* Yesterday's value - clickable in swap mode */}
                            {yesterday !== '' && (
                              <div
                                onClick={() => handleSwapClick(denom, slotIdx)}
                                className={`text-[8px] font-mono mb-0.5 rounded-xl px-1 ${
                                  swapMode
                                    ? isSwapSelected
                                      ? 'bg-orange-600 text-white cursor-pointer font-bold'
                                      : isSwapTarget
                                        ? 'bg-orange-600/20 text-orange-400 cursor-pointer hover:bg-orange-600/40 border border-orange-500/50'
                                        : `${yesterdayOverPack ? 'text-red-400 font-bold' : 'text-muted-foreground/50'} cursor-pointer hover:bg-orange-600/20`
                                    : yesterdayOverPack ? 'text-red-400 font-bold' : 'text-muted-foreground/50'
                                }`}
                                title={yesterdayOverPack ? `Yesterday's number (${yesterday}) exceeds this slot's pack size of ${slotPack}` : undefined}
                                data-testid={`yesterday-${denom}-${slotIdx}`}
                              >
                                {yesterday}
                              </div>
                            )}
                            <Input
                              type="text"
                              inputMode="numeric"
                              data-grid-row={slotIdx}
                              data-grid-col={denom}
                              data-slot-denom={denom}
                              data-slot-idx={slotIdx}
                              value={val}
                              onChange={(e) => updateRunningNumber(denom, slotIdx, e.target.value)}
                              onClick={(e) => handleCellClick(denom, slotIdx, e)}
                              onFocus={() => {
                                lastFocusedCellRef.current = { denom, slotIdx };
                                if (voice.isListening) setVoiceTargetCell({ denom, slotIdx });
                              }}
                              onBlur={() => normalizeRunningOnBlur(denom, slotIdx)}
                              onKeyDown={(e) => handleKeyDown(e, denom, slotIdx)}
                              onPaste={(e) => handlePaste(e, denom, slotIdx)}
                              onDoubleClick={() => { handleDoubleClick(denom, slotIdx); showQtyCell(denom, slotIdx); }}
                              className={`h-7 w-14 text-center text-xs font-mono p-0 rounded-xl ${isSelected ? 'ring-2 ring-primary' : ''} ${isWrapped ? `bg-white font-bold ${bothOverPack ? '' : 'text-black'}` : isSelected ? 'bg-primary/20' : val === '000' ? 'bg-blue-500/20' : ''} ${bothOverPack ? 'input-negative font-bold' : ''} ${voice.isListening && voiceTargetCell?.denom === denom && voiceTargetCell?.slotIdx === slotIdx ? 'ring-2 ring-red-500 bg-red-500/10' : ''}`}
                              data-over-pack={bothOverPack ? 'true' : undefined}
                              placeholder="0"
                              title={bothOverPack
                                ? `Over pack size: today (${val}) and yesterday (${yesterday}) both exceed this slot's pack size of ${slotPack}. Check the number or set the correct pack quantity (double-click the cell).`
                                : "Empty or 0 = no pack in this slot. Type 000 (or 'new') for a full new pack."}
                            />
                            {val !== '' && sales > 0 && !isFirstDay && (
                              <span className="absolute -bottom-0.5 right-0.5 text-[8px] text-primary font-bold">{sales}</span>
                            )}
                            {val !== '' && (() => {
                              const isNewPack = (val !== '' && yesterday === '') || isWrapped;
                              const yPQ = yesterdayPackQuantities[denom]?.[slotIdx] || '';
                              const curPQ = packQuantities[denom]?.[slotIdx] || '';
                              // Show yesterday's qty as inherited only if user hasn't touched this cell
                              const effectiveVal = curPQ !== '' ? curPQ : (!isNewPack && yPQ ? yPQ : '');
                              const needsEntry = isNewPack && !curPQ;
                              const qtyKey = `${denom}-${slotIdx}`;
                              // Respect user preference: when showQtyCells is off, only show on explicit double-click
                              const isVisible = showQtyCells
                                ? (needsEntry || visibleQtyCells[qtyKey])
                                : !!visibleQtyCells[qtyKey];
                              if (!isVisible) return null;
                              return (
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={effectiveVal}
                                  onChange={(e) => {
                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                    saveUndoPoint();
                                    markEdited();
                                    setPackQuantities(prev => {
                                      const newArr = [...(prev[denom] || Array(MAX_SLOTS).fill(''))];
                                      newArr[slotIdx] = raw;
                                      return { ...prev, [denom]: newArr };
                                    });
                                  }}
                                  onFocus={() => { const k = `${denom}-${slotIdx}`; if (qtyTimers.current[k]) clearTimeout(qtyTimers.current[k]); setVisibleQtyCells(p => ({ ...p, [k]: true })); }}
                                  onBlur={() => showQtyCell(denom, slotIdx)}
                                  className={`w-8 h-4 text-[9px] font-mono text-center rounded mt-0.5 border-0 outline-none ${needsEntry ? 'bg-amber-500/40 text-amber-200 ring-1 ring-amber-400 animate-pulse' : 'bg-[hsl(148,25%,42%)] text-black'}`}
                                  placeholder="qty"
                                  title="Tickets in pack (e.g. 20 or 30)"
                                  data-testid={`pq-${denom}-${slotIdx}`}
                                />
                              );
                            })()}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="border-t-2 border-primary/30 bg-secondary/50">
                  <td className="px-0.5 py-1 text-xs font-bold">Tot</td>
                  {DENOMINATIONS.map(denom => {
                    const sales = getDenomSales(denom);
                    return (
                      <td key={denom} className="px-0.5 py-1 text-center">
                        <div className="text-xs font-mono font-bold text-card-foreground">{showCalculations ? sales : ''}</div>
                        <div className="text-xs text-primary font-bold">{showCalculations ? `$${sales * denom}` : ''}</div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile: one vertical card per denomination — thumb-friendly inputs */}
          <div className="sm:hidden space-y-3 p-1" data-testid="mobile-running-cards">
            {DENOMINATIONS.map(denom => {
              const visible = getVisibleCount(denom);
              const denomSales = getDenomSales(denom);
              return (
                <div key={denom} className="rounded-xl border border-border/60 bg-secondary/30 overflow-hidden" data-testid={`m-denom-card-${denom}`}>
                  <div className="flex items-center justify-between px-3 py-2 bg-secondary/70">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono font-bold text-lg text-primary" data-testid={`m-denom-price-${denom}`}>${denom}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">tickets · 0–{PACK_SIZES[denom] - 1}</span>
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground">Pack {PACK_SIZES[denom]}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 p-2">
                    {Array.from({ length: visible }).map((_, slotIdx) => {
                      const val = runningNumbers[denom]?.[slotIdx] || '';
                      const yesterday = yesterdayNumbers[denom]?.[slotIdx] || '';
                      const sales = getSlotSales(denom, slotIdx);
                      const isWrapped = val !== '' && parseInt(val) < parseInt(yesterday);
                      const slotPack = getSlotPackSize(denom, slotIdx);
                      const bothOverPack = val !== '' && yesterday !== '' && parseInt(val) > slotPack && parseInt(yesterday) > slotPack;
                      const yesterdayOverPack = yesterday !== '' && parseInt(yesterday) > slotPack;
                      const isNewPack = (val !== '' && yesterday === '') || isWrapped;
                      const curPQ = packQuantities[denom]?.[slotIdx] || '';
                      const needsEntry = isNewPack && !curPQ;
                      return (
                        <div key={slotIdx} className="flex flex-col">
                          <div className="flex items-center justify-between px-1">
                            <span className="text-[10px] font-mono text-muted-foreground">#{slotIdx + 1}</span>
                            {yesterday !== '' && (
                              <span className={`text-[10px] font-mono ${yesterdayOverPack ? 'text-red-400 font-bold' : 'text-muted-foreground/60'}`} data-testid={`m-yesterday-${denom}-${slotIdx}`}>{yesterday}</span>
                            )}
                          </div>
                          <div className="relative">
                            <Input
                              type="text"
                              inputMode="numeric"
                              data-m-slot={`${denom}-${slotIdx}`}
                              value={val}
                              onChange={(e) => updateRunningNumber(denom, slotIdx, e.target.value)}
                              onFocus={() => {
                                lastFocusedCellRef.current = { denom, slotIdx };
                                if (voice.isListening) setVoiceTargetCell({ denom, slotIdx });
                              }}
                              onBlur={() => normalizeRunningOnBlur(denom, slotIdx)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const next = document.querySelector(`[data-m-slot="${denom}-${slotIdx + 1}"]`);
                                  if (next) next.focus();
                                }
                              }}
                              onPaste={(e) => handlePaste(e, denom, slotIdx)}
                              className={`h-11 w-full text-center text-base font-mono p-0 rounded-lg ${isWrapped ? `bg-white font-bold ${bothOverPack ? '' : 'text-black'}` : val === '000' ? 'bg-blue-500/20' : ''} ${bothOverPack ? 'input-negative font-bold' : ''}`}
                              placeholder="0"
                              title={bothOverPack
                                ? `Over pack size: today (${val}) and yesterday (${yesterday}) both exceed this slot's pack size of ${slotPack}.`
                                : "Empty or 0 = no pack in this slot. Type 000 (or 'new') for a full new pack."}
                              data-testid={`m-running-${denom}-${slotIdx}`}
                            />
                            {val !== '' && sales > 0 && !isFirstDay && (
                              <span className="absolute bottom-0.5 right-1 text-[9px] text-primary font-bold">{sales}</span>
                            )}
                          </div>
                          {val !== '' && (needsEntry || (isNewPack && curPQ !== '')) && (
                            <input
                              type="text"
                              inputMode="numeric"
                              value={curPQ}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/[^0-9]/g, '');
                                saveUndoPoint();
                                markEdited();
                                setPackQuantities(prev => {
                                  const newArr = [...(prev[denom] || Array(MAX_SLOTS).fill(''))];
                                  newArr[slotIdx] = raw;
                                  return { ...prev, [denom]: newArr };
                                });
                              }}
                              className={`h-8 mt-1 w-full text-sm font-mono text-center rounded-lg border-0 outline-none ${needsEntry ? 'bg-amber-500/40 text-amber-200 ring-1 ring-amber-400 animate-pulse' : 'bg-[hsl(148,25%,42%)] text-black'}`}
                              placeholder="pack qty"
                              title="Tickets in pack (e.g. 20 or 30)"
                              data-testid={`m-pq-${denom}-${slotIdx}`}
                            />
                          )}
                        </div>
                      );
                    })}
                    {visible < MAX_SLOTS && (
                      <button
                        type="button"
                        onClick={() => setExpandedRows(prev => ({ ...prev, [denom]: Math.min(MAX_SLOTS, visible + 1) }))}
                        className="h-11 self-end rounded-lg border border-dashed border-border/70 text-muted-foreground font-mono text-xs active:bg-secondary"
                        data-testid={`m-add-slot-${denom}`}
                      >
                        + slot
                      </button>
                    )}
                  </div>
                  {showCalculations && denomSales > 0 && (
                    <div className="flex items-center justify-between px-3 py-1.5 border-t border-border/40 bg-secondary/50 font-mono text-xs" data-testid={`m-denom-total-${denom}`}>
                      <span className="text-muted-foreground">Sold {denomSales}</span>
                      <span className="text-primary font-bold">${denomSales * denom}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Right column: Online Reports + Sales Comparison + Inventory stacked */}
      <div className="flex flex-col gap-4 flex-1 min-w-0 w-full">
      {voiceLog.length > 0 && (
        <div className={`bg-card/60 border ${voice.isListening ? 'border-red-500/30' : 'border-border/50'} rounded-lg px-3 py-2`} data-testid="voice-log">
          <div className="flex items-center justify-between mb-1">
            <span className={`text-[10px] font-mono uppercase tracking-wider ${voice.isListening ? 'text-red-300/80' : 'text-muted-foreground'}`}>
              Voice log · last {voiceLog.length}{!voice.isListening && ' (paused)'}
            </span>
            <button
              type="button"
              onClick={() => setVoiceLog([])}
              className="text-[10px] font-mono text-muted-foreground hover:text-foreground"
              data-testid="voice-log-clear"
            >
              clear
            </button>
          </div>
          <ul className="space-y-0.5">
            {voiceLog.map((entry, i) => (
              <li
                key={entry.t}
                className={`flex items-center gap-2 text-[11px] font-mono ${i === 0 ? 'text-foreground' : 'text-muted-foreground'}`}
                data-testid={`voice-log-row-${i}`}
              >
                <span className={voice.isListening ? 'text-red-400/80' : 'text-muted-foreground/60'}>▶</span>
                <span className="truncate max-w-[40%]" title={entry.heard}>“{entry.heard}”</span>
                <span className="text-muted-foreground/60">→</span>
                <span className="truncate flex-1" title={entry.action}>{entry.action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-2">
      {/* Machine Block */}
      <Card className="border-0 shadow-lg flex-1 min-w-0">
        <CardHeader className="py-2 px-2">
          <CardTitle className="font-mono text-sm">Online Reports Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <table className="border-collapse w-full table-fixed" style={{ borderSpacing: 0 }}>
            <thead>
              <tr className="bg-muted/30">
                <th className="py-1 text-center text-[9px] font-mono"></th>
                <th className="py-1 text-center">
                  <ColumnNotePopover
                    noteKey="online_machine1"
                    label={<span className="th-pill">Machine-1</span>}
                    note={columnNotes.online_machine1}
                    isAdmin={isAdmin}
                    onSave={saveColumnNote}
                  />
                </th>
                <th className="py-1 text-center">
                  <ColumnNotePopover
                    noteKey="online_machine2"
                    label={<span className="th-pill">Machine-2</span>}
                    note={columnNotes.online_machine2}
                    isAdmin={isAdmin}
                    onSave={saveColumnNote}
                  />
                </th>
                <th className="py-1 text-center"><span className="th-pill">Total</span></th>
              </tr>
            </thead>
            <tbody>
              {MACHINE_ROWS.map(row => {
                const m1 = parseFloat(machineData[row]?.machine1) || 0;
                const m2 = parseFloat(machineData[row]?.machine2) || 0;
                const m1Neg = m1 < 0;
                const m2Neg = m2 < 0;
                const m1Raw = machineData[row]?.machine1 || '';
                const m2Raw = machineData[row]?.machine2 || '';
                return (
                  <tr key={row} className="border-t border-border/30">
                    <td className="px-1 py-0.5 text-[9px] font-mono font-bold text-primary">{MACHINE_ROW_LABELS[row]}</td>
                    <td className="px-0.5 text-center">
                      <Input
                        type="text" inputMode="numeric"
                        value={m1Neg ? (focusedMachineCell === `${row}-machine1` ? m1Raw : `(${fmtNum(Math.abs(m1))})`) : m1Raw}
                        onChange={(e) => updateMachine(row, 'machine1', e.target.value)}
                        onFocus={() => setFocusedMachineCell(`${row}-machine1`)}
                        onBlur={() => setFocusedMachineCell(null)}
                        onPaste={(e) => handleMachinePaste(e, row, 'machine1')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const idx = MACHINE_ROWS.indexOf(row);
                            if (idx < MACHINE_ROWS.length - 1) {
                              setTimeout(() => {
                                const next = document.querySelector(`[data-machine-row="${MACHINE_ROWS[idx + 1]}"][data-machine-col="machine1"]`);
                                if (next) next.focus();
                              }, 50);
                            }
                          }
                        }}
                        data-machine-row={row}
                        data-machine-col="machine1"
                        className={`h-7 w-full text-center text-xs font-mono p-0 rounded-xl ${m1Neg ? 'input-negative' : ''}`}
                        placeholder="0"
                      />
                    </td>
                    <td className="px-0.5 text-center">
                      <Input
                        type="text" inputMode="numeric"
                        value={m2Neg ? (focusedMachineCell === `${row}-machine2` ? m2Raw : `(${fmtNum(Math.abs(m2))})`) : m2Raw}
                        onChange={(e) => updateMachine(row, 'machine2', e.target.value)}
                        onFocus={() => setFocusedMachineCell(`${row}-machine2`)}
                        onBlur={() => setFocusedMachineCell(null)}
                        onPaste={(e) => handleMachinePaste(e, row, 'machine2')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const idx = MACHINE_ROWS.indexOf(row);
                            if (idx < MACHINE_ROWS.length - 1) {
                              setTimeout(() => {
                                const next = document.querySelector(`[data-machine-row="${MACHINE_ROWS[idx + 1]}"][data-machine-col="machine2"]`);
                                if (next) next.focus();
                              }, 50);
                            }
                          }
                        }}
                        data-machine-row={row}
                        data-machine-col="machine2"
                        className={`h-7 w-full text-center text-xs font-mono p-0 rounded-xl ${m2Neg ? 'input-negative' : ''}`}
                        placeholder="0"
                      />
                    </td>
                    <td className="px-0.5 text-center">
                      {(() => {
                        const total = m1 + m2;
                        const isNeg = total < 0;
                        return (
                          <div className={`h-7 w-full mx-auto flex items-center justify-center font-mono text-xs font-bold rounded-xl ${isNeg && showCalculations ? 'text-red-700 bg-red-100 ring-1 ring-red-300 dark:text-red-200 dark:bg-red-950/60 dark:ring-red-700/60' : 'bg-secondary text-card-foreground'}`}>
                            {showCalculations ? renderNum(total) : ''}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Sales Comparison Block */}
      <Card className="border-[3px] border-red-600 shadow-lg flex-1 min-w-0 bg-teal-700/35 dark:bg-teal-700/30 ring-1 ring-red-500/40" data-testid="sales-comparison-card">
        <CardHeader className="py-2 px-2">
          <CardTitle className="font-mono text-sm font-extrabold text-slate-900 dark:text-emerald-100 drop-shadow-none">
            Sales Comparison <span className="text-[#ff3f1a] drop-shadow-[0_1px_0_rgba(0,0,0,0.25)]">(Final Result)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <table className="border-collapse w-full table-fixed" style={{ borderSpacing: 0 }}>
            <thead>
              <tr className="bg-muted/30">
                <th className="py-1 text-center text-[9px] font-mono"></th>
                <th className="py-1 text-center">
                  <ColumnNotePopover
                    noteKey="comp_actual"
                    label={<span className="th-pill">Actual</span>}
                    note={columnNotes.comp_actual}
                    isAdmin={isAdmin}
                    onSave={saveColumnNote}
                  />
                </th>
                <th className="py-1 text-center">
                  <ColumnNotePopover
                    noteKey="comp_register"
                    label={<span className="th-pill">Register</span>}
                    note={columnNotes.comp_register}
                    isAdmin={isAdmin}
                    onSave={saveColumnNote}
                  />
                </th>
                <th className="py-1 text-center">
                  <ColumnNotePopover
                    noteKey="comp_difference"
                    label={<span className="th-pill">Difference</span>}
                    note={columnNotes.comp_difference}
                    isAdmin={isAdmin}
                    onSave={saveColumnNote}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {SALES_ROWS.map(row => {
                const computedActuals = getComputedSalesActual();
                const actual = computedActuals[row];
                const registerRaw = salesComparison[row]?.register || '';
                const registerNum = parseFloat(registerRaw) || 0;
                const registerNeg = registerNum < 0;
                const register = registerNum;
                const diff = (row === 'instantSale' || row === 'onlineSale') ? register - actual : actual - register;
                return (
                  <tr key={row} className="border-t border-border/30">
                    <td className="px-1 py-1 text-xs font-mono font-extrabold text-primary leading-tight">{SALES_ROW_LABELS[row]}</td>
                    <td className="px-0.5 text-center">
                      <div className={`h-7 w-full mx-auto flex items-center justify-center font-mono text-xs font-bold rounded-xl ${actual < 0 && showCalculations ? 'text-red-700 bg-red-100 ring-1 ring-red-300 dark:text-red-200 dark:bg-red-950/60 dark:ring-red-700/60' : 'text-primary bg-secondary'}`}>
                        {showCalculations ? renderNum(actual) : ''}
                      </div>
                    </td>
                    <td className="px-0.5 text-center">
                      <Input
                        type="text" inputMode="numeric"
                        value={registerNeg ? (focusedSalesCell === row ? registerRaw : `(${fmtNum(Math.abs(register))})`) : registerRaw}
                        onChange={(e) => updateSalesComparison(row, 'register', e.target.value)}
                        onPaste={(e) => handleSalesPaste(e, row)}
                        onFocus={() => setFocusedSalesCell(row)}
                        onBlur={() => setFocusedSalesCell(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const idx = SALES_ROWS.indexOf(row);
                            if (idx < SALES_ROWS.length - 1) {
                              setTimeout(() => {
                                const next = document.querySelector(`[data-sales-row="${SALES_ROWS[idx + 1]}"][data-sales-col="register"]`);
                                if (next) next.focus();
                              }, 50);
                            }
                          }
                        }}
                        data-sales-row={row}
                        data-sales-col="register"
                        className={`h-7 w-full text-center text-xs font-mono p-0 rounded-xl ${registerNeg ? 'input-negative' : ''}`}
                        placeholder="0"
                      />
                    </td>
                    <td className="px-0.5 text-center">
                      <div className={`h-7 w-full mx-auto flex items-center justify-center font-mono text-xs font-bold rounded-xl ${diff > 0 && showCalculations ? 'text-emerald-800 bg-emerald-100 ring-1 ring-emerald-300 dark:text-emerald-200 dark:bg-emerald-900/60 dark:ring-emerald-700/60' : diff < 0 && showCalculations ? 'text-red-700 bg-red-100 ring-1 ring-red-300 dark:text-red-200 dark:bg-red-950/60 dark:ring-red-700/60' : 'text-slate-700 bg-slate-100 ring-1 ring-slate-300 dark:text-muted-foreground dark:bg-secondary dark:ring-transparent'}`}>
                        {showCalculations ? renderNum(diff) : ''}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </div>

      {/* Inventory Section - under Online Reports & Sales Comparison */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="py-2 px-2">
          <CardTitle className="font-mono text-sm">Inventory</CardTitle>
        </CardHeader>
        <CardContent className="p-0 px-1 pb-1">
            <table className="border-collapse w-full table-fixed" style={{ borderSpacing: 0 }}>
              <thead>
                <tr className="bg-muted/30">
                  <th className="py-0.5 text-center"><span className="th-pill">Value</span></th>
                  <th className="py-0.5 text-center"><span className="th-pill">Opening</span></th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_delivery" label={<span className="th-pill">Delivery</span>}
                      note={columnNotes.inv_delivery} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_ticket_plus" label={<span className="th-pill">Ticket+</span>}
                      note={columnNotes.inv_ticket_plus} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_ticket_minus" label={<span className="th-pill">Ticket-</span>}
                      note={columnNotes.inv_ticket_minus} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_lvm1" label={<span className="th-pill">LVM-1</span>}
                      note={columnNotes.inv_lvm1} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_lvm2" label={<span className="th-pill">LVM-2</span>}
                      note={columnNotes.inv_lvm2} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_ending" label={<span className="th-pill">Ending</span>}
                      note={columnNotes.inv_ending} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_actual_dollar" label={<span className="th-pill">Actual$</span>}
                      note={columnNotes.inv_actual_dollar} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_register_dollar" label={<span className="th-pill">Register$</span>}
                      note={columnNotes.inv_register_dollar} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                  <th className="py-0.5 text-center">
                    <ColumnNotePopover noteKey="inv_difference" label={<span className="th-pill">Difference</span>}
                      note={columnNotes.inv_difference} isAdmin={isAdmin} onSave={saveColumnNote} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {DENOMINATIONS.map(denom => {
                  const deliveryPacks = parseInt(inventory[denom]?.delivery) || 0;
                  const lvm1Packs = parseInt(inventory[denom]?.lvm) || 0;
                  const lvm2Packs = parseInt(inventory[denom]?.lvm2) || 0;
                  const lvmPacks = lvm1Packs + lvm2Packs;
                  const endingPacks = parseInt(inventory[denom]?.endingInventory) || 0;
                  const deliveryDollars = deliveryPacks * PACK_SIZES[denom] * denom;
                  const lvm1Dollars = lvm1Packs * PACK_SIZES[denom] * denom;
                  const lvm2Dollars = lvm2Packs * PACK_SIZES[denom] * denom;
                  const endingDollars = endingPacks * PACK_SIZES[denom] * denom;
                  const yesterdayEndingPacks = parseInt(yesterdayInventory[denom]?.endingInventory) || 0;
                  const yesterdayEndingDollars = yesterdayEndingPacks * PACK_SIZES[denom] * denom;
                  const { total: actualSaleDollars, breakdown: actualBreakdown } = getDenomActualSaleDollars(denom, true);
                  const registerSale = parseFloat(inventory[denom]?.registerSale) || 0;
                  const difference = registerSale - actualSaleDollars;
                  return (
                    <tr key={denom} className="border-t border-border/30">
                      <td className="px-0 text-center">
                        <div className="h-7 w-full mx-auto flex items-center justify-center font-mono text-[10px] font-bold text-primary">${denom}</div>
                      </td>
                      <td className="px-0 text-center">
                        <div className="h-7 w-full mx-auto flex items-center justify-center font-mono text-[10px] font-bold rounded-xl bg-secondary text-card-foreground" data-testid={`opening-inv-${denom}`}>
                          {!isFirstDay ? yesterdayEndingPacks : ''}
                        </div>
                      </td>
                      <td className="px-0 text-center">
                        <div className="relative inline-block">
                          <Input
                            type="text" inputMode="numeric"
                            min="0"
                            value={inventory[denom]?.delivery || ''}
                            onChange={(e) => updateInventory(denom, 'delivery', e.target.value)}
                            onPaste={(e) => handleInventoryPaste(e, denom, 'delivery')}
                            onKeyDown={(e) => handleInventoryKeyDown(e, denom, 'delivery')}
                            data-inv-denom={denom}
                            data-inv-field="delivery"
                            className="h-7 w-full text-center text-xs font-mono p-0 rounded-xl"
                            placeholder="0"
                          />
                          {(parseInt(inventory[denom]?.delivery) || 0) > 0 && (
                            <span className="absolute -bottom-0.5 right-0.5 text-[7px] text-primary font-bold">${(parseInt(inventory[denom]?.delivery) || 0) * PACK_SIZES[denom] * denom}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-0 text-center">
                        <Input
                          type="text" inputMode="numeric"
                          min="0"
                          value={inventory[denom]?.ticketAdded || ''}
                          onChange={(e) => updateInventory(denom, 'ticketAdded', e.target.value)}
                          onPaste={(e) => handleInventoryPaste(e, denom, 'ticketAdded')}
                          onKeyDown={(e) => handleInventoryKeyDown(e, denom, 'ticketAdded')}
                          data-inv-denom={denom}
                          data-inv-field="ticketAdded"
                          className="h-7 w-full text-center text-xs font-mono p-0 rounded-xl"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-0 text-center">
                        <Input
                          type="text" inputMode="numeric"
                          min="0"
                          value={inventory[denom]?.ticketReturned || ''}
                          onChange={(e) => updateInventory(denom, 'ticketReturned', e.target.value)}
                          onPaste={(e) => handleInventoryPaste(e, denom, 'ticketReturned')}
                          onKeyDown={(e) => handleInventoryKeyDown(e, denom, 'ticketReturned')}
                          data-inv-denom={denom}
                          data-inv-field="ticketReturned"
                          className={`h-7 w-full text-center text-xs font-mono p-0 rounded-xl ${(parseInt(inventory[denom]?.ticketReturned) || 0) > 0 ? 'input-pink' : ''}`}
                          placeholder="0"
                        />
                      </td>
                      <td className="px-0 text-center">
                        <div className="relative inline-block">
                          <Input
                            type="text" inputMode="numeric"
                            min="0"
                            value={inventory[denom]?.lvm || ''}
                            onChange={(e) => updateInventory(denom, 'lvm', e.target.value)}
                            onPaste={(e) => handleInventoryPaste(e, denom, 'lvm')}
                            onKeyDown={(e) => handleInventoryKeyDown(e, denom, 'lvm')}
                            data-inv-denom={denom}
                            data-inv-field="lvm"
                            className="h-7 w-full text-center text-xs font-mono p-0 rounded-xl"
                            placeholder="0"
                            title="LVM-1"
                          />
                          {lvm1Packs > 0 && (
                            <span className="absolute -bottom-0.5 right-0.5 text-[7px] text-primary font-bold">${lvm1Dollars}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-0 text-center">
                        <div className="relative inline-block">
                          <Input
                            type="text" inputMode="numeric"
                            min="0"
                            value={inventory[denom]?.lvm2 || ''}
                            onChange={(e) => updateInventory(denom, 'lvm2', e.target.value)}
                            onPaste={(e) => handleInventoryPaste(e, denom, 'lvm2')}
                            onKeyDown={(e) => handleInventoryKeyDown(e, denom, 'lvm2')}
                            data-inv-denom={denom}
                            data-inv-field="lvm2"
                            className="h-7 w-full text-center text-xs font-mono p-0 rounded-xl"
                            placeholder="0"
                            title="LVM-2 (second vending machine — leave blank if only one)"
                          />
                          {lvm2Packs > 0 && (
                            <span className="absolute -bottom-0.5 right-0.5 text-[7px] text-primary font-bold">${lvm2Dollars}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-0 text-center">
                        <div className="relative inline-block">
                          <Input
                            type="text" inputMode="numeric"
                            min="0"
                            value={inventory[denom]?.endingInventory || ''}
                            onChange={(e) => updateInventory(denom, 'endingInventory', e.target.value)}
                            onPaste={(e) => handleInventoryPaste(e, denom, 'endingInventory')}
                            onKeyDown={(e) => handleInventoryKeyDown(e, denom, 'endingInventory')}
                            data-inv-denom={denom}
                            data-inv-field="endingInventory"
                            className="h-7 w-full text-center text-xs font-mono p-0 rounded-xl"
                            placeholder="0"
                          />
                          {(parseInt(inventory[denom]?.endingInventory) || 0) > 0 && (
                            <span className="absolute -bottom-0.5 right-0.5 text-[7px] text-primary font-bold">${(parseInt(inventory[denom]?.endingInventory) || 0) * PACK_SIZES[denom] * denom}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-0 text-center">
                        <div className={`h-7 w-full mx-auto flex items-center justify-center font-mono text-xs font-bold rounded-xl cursor-help ${actualSaleDollars < 0 && showCalculations ? 'text-red-700 bg-red-100 ring-1 ring-red-300 dark:text-red-200 dark:bg-red-950/60 dark:ring-red-700/60' : 'text-primary bg-secondary'}`} data-testid={`actual-sale-${denom}`} title={actualBreakdown}>
                          {showCalculations ? renderNum(actualSaleDollars) : ''}
                        </div>
                      </td>
                      <td className="px-0 text-center">
                        {(() => {
                          const regRaw = inventory[denom]?.registerSale || '';
                          const regNum = parseFloat(regRaw) || 0;
                          const regNeg = regNum < 0;
                          const cellKey = `${denom}-registerSale`;
                          const showFormatted = regNeg && focusedInventoryCell !== cellKey;
                          return (
                            <Input
                              type="text" inputMode="numeric"
                              min="0"
                              value={showFormatted ? `(${fmtNum(Math.abs(regNum))})` : regRaw}
                              onChange={(e) => updateInventory(denom, 'registerSale', e.target.value)}
                              onPaste={(e) => handleInventoryPaste(e, denom, 'registerSale')}
                              onKeyDown={(e) => handleInventoryKeyDown(e, denom, 'registerSale')}
                              onFocus={() => setFocusedInventoryCell(cellKey)}
                              onBlur={() => setFocusedInventoryCell(null)}
                              data-inv-denom={denom}
                              data-inv-field="registerSale"
                              className={`h-7 w-full text-center text-xs font-mono p-0 rounded-xl ${regNeg ? 'input-negative' : ''}`}
                              data-testid={`register-sale-${denom}`}
                              placeholder="0"
                            />
                          );
                        })()}
                      </td>
                      <td className="px-0 text-center">
                        <div className={`h-7 w-full mx-auto flex items-center justify-center font-mono text-xs font-bold rounded-xl ${difference > 0 && showCalculations ? 'text-emerald-800 bg-emerald-100 ring-1 ring-emerald-300 dark:text-emerald-200 dark:bg-emerald-900/60 dark:ring-emerald-700/60' : difference < 0 && showCalculations ? 'text-red-700 bg-red-100 ring-1 ring-red-300 dark:text-red-200 dark:bg-red-950/60 dark:ring-red-700/60' : 'text-slate-700 bg-slate-100 ring-1 ring-slate-300 dark:text-muted-foreground dark:bg-secondary dark:ring-transparent'}`} data-testid={`difference-${denom}`}>
                          {showCalculations ? renderNum(difference) : ''}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot data-testid="inventory-totals-row">
                {(() => {
                  let openP = 0, openV = 0, delP = 0, delV = 0;
                  let tktP = 0, tktV = 0, tktRetP = 0, tktRetV = 0;
                  let lvm1P = 0, lvm1V = 0, lvm2P = 0, lvm2V = 0;
                  let endP = 0, endV = 0;
                  let actualSum = 0, registerSum = 0, diffSum = 0;
                  DENOMINATIONS.forEach(d => {
                    const ps = PACK_SIZES[d];
                    const op = parseInt(yesterdayInventory[d]?.endingInventory) || 0;
                    const dp = parseInt(inventory[d]?.delivery) || 0;
                    const ta = parseInt(inventory[d]?.ticketAdded) || 0;
                    const tr = parseInt(inventory[d]?.ticketReturned) || 0;
                    const l1 = parseInt(inventory[d]?.lvm) || 0;
                    const l2 = parseInt(inventory[d]?.lvm2) || 0;
                    const ep = parseInt(inventory[d]?.endingInventory) || 0;
                    openP += op;  openV += op * ps * d;
                    delP += dp;   delV += dp * ps * d;
                    tktP += ta;   tktV += ta * d;
                    tktRetP += tr; tktRetV += tr * d;
                    lvm1P += l1;  lvm1V += l1 * ps * d;
                    lvm2P += l2;  lvm2V += l2 * ps * d;
                    endP += ep;   endV += ep * ps * d;
                    const a = (typeof getDenomActualSaleDollars === 'function') ? (getDenomActualSaleDollars(d) || 0) : 0;
                    const r = parseFloat(inventory[d]?.registerSale) || 0;
                    actualSum += a;
                    registerSum += r;
                    diffSum += (r - a);
                  });
                  const cell = (packs, dollars, key) => (
                    <td key={key} className="px-0.5 py-1 text-center font-mono text-[10px]">
                      <div className="font-bold text-primary leading-tight">{packs}</div>
                      <div className="text-[8px] text-muted-foreground leading-tight">${dollars}</div>
                    </td>
                  );
                  return (
                    <tr className="border-t-2 border-primary/40 bg-secondary/40" data-testid="inventory-totals">
                      <td className="px-1 py-1 text-[10px] font-mono font-bold text-primary uppercase tracking-wide">Total</td>
                      {cell(openP, openV, 'open')}
                      {cell(delP, delV, 'del')}
                      {cell(tktP, tktV, 'tkt+')}
                      {cell(tktRetP, tktRetV, 'tkt-')}
                      {cell(lvm1P, lvm1V, 'lvm1')}
                      {cell(lvm2P, lvm2V, 'lvm2')}
                      {cell(endP, endV, 'end')}
                      <td className={`px-0.5 py-1 text-center font-mono text-[10px] ${actualSum < 0 ? 'text-red-700 font-extrabold' : 'text-primary font-bold'}`}>
                        {actualSum < 0 ? `($${Math.abs(actualSum).toLocaleString()})` : `$${actualSum.toLocaleString()}`}
                      </td>
                      <td className={`px-0.5 py-1 text-center font-mono text-[10px] ${registerSum < 0 ? 'text-red-700 font-extrabold' : 'text-primary font-bold'}`}>
                        ${registerSum.toLocaleString(undefined, {maximumFractionDigits: 2})}
                      </td>
                      <td className={`px-0.5 py-1 text-center font-mono text-[10px] ${diffSum < 0 ? 'text-red-700 font-extrabold' : 'text-primary font-bold'}`}>
                        {diffSum < 0 ? `($${Math.abs(diffSum).toLocaleString(undefined, {maximumFractionDigits: 2})})` : `$${diffSum.toLocaleString(undefined, {maximumFractionDigits: 2})}`}
                      </td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
        </CardContent>
      </Card>
      </div>
      </div>

      {/* Auto-save Status */}
      <div className="flex justify-end items-center gap-2 text-xs font-mono text-muted-foreground">
        {saveStatus === 'saving' && <><div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Saving...</>}
        {saveStatus === 'saved' && <><Check className="w-3 h-3 text-primary" /> Saved</>}
        {saveStatus === '' && <span>Auto-save enabled</span>}
      </div>

      {/* Clear Page Confirmation Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={(open) => { if (!open) closeClearDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear This Page ({dateStr})?</DialogTitle>
            <DialogDescription>
              This will clear all data entered for {dateStr}. The data is moved to the
              30-day Recovery bin and can be restored from <strong>Admin → Recovery</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs font-mono text-muted-foreground">
              Type your account email <span className="font-bold text-destructive">{authUser?.email}</span> to confirm:
            </p>
            <Input
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              placeholder={authUser?.email || "Type your email"}
              className="font-mono"
              data-testid="clear-page-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeClearDialog}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleClearPage}
              disabled={clearConfirmText.trim().toLowerCase() !== (authUser?.email || '').trim().toLowerCase() || clearing}
              data-testid="clear-page-final-confirm"
            >
              {clearing ? 'Clearing...' : 'Clear This Page'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
